import React, { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { EstabelecimentoDescricaoEditor } from '../components/EstabelecimentoDescricaoEditor';
import { StatusProgramadoAgendaPanel } from '../components/StatusProgramadoAgendaPanel';
import { useRefetchWhenCatalogChanges } from '../contexts/AdminCatalogContext';
import { useAdminUnsaved } from '../contexts/AdminUnsavedContext';
import { useAdminDialog } from '../contexts/AdminDialogContext';
import { getCidades, type Cidade } from '../lib/experienciasStorage';
import {
  buildNoticiaLocalizacaoPicks,
  EMPTY_NOTICIA_LOCALIZACAO_FORM,
  formPatchFromNoticiaLocalizacaoPick,
  labelLocalizacaoResumo,
  searchNoticiaLocalizacoes,
  type NoticiaLocalizacaoPick,
} from '../lib/noticiaLocalizacaoSearchApi';
import {
  addNewsArticle,
  addNewsCategory,
  addNewsSubcategory,
  addNewsTagToRegistry,
  removeNewsCategory,
  removeNewsSubcategory,
  updateNewsCategory,
  updateNewsSubcategory,
  getComercioRestauranteUsers,
  getNewsArticleById,
  getNewsArticles,
  getNewsCategories,
  getNewsSubcategories,
  getNewsSubcategoriesByCategory,
  getNewsTagRegistry,
  isNewsAtiva,
  mergeNewsArticleSubcategoryIds,
  removeNewsArticle,
  removeNewsTagCompletely,
  removeNewsTagFromRegistryOnly,
  renameNewsTagInRegistry,
  saveNewsArticle,
  associacoesFromArticle,
  type NewsArticle,
  type NewsResponsibleUser,
  type NewsCategory,
  type NewsLayoutType,
  type NewsLocalizacaoEscopo,
  type NewsSubcategory,
} from '../lib/newsStorage';
import { getComercioProfile } from '../lib/comercioProfilesStorage';
import { getEstabelecimentos, type Estabelecimento } from '../lib/estabelecimentosStorage';
import { getHospedagens, type Hospedagem } from '../lib/hospedagemStorage';
import { getEvents, type Event } from '../lib/eventsStorage';
import { getAgencias, getGuias, type Agencia, type Guia } from '../lib/guiasAgenciasStorage';
import {
  hojeYmdLocal,
  MSG_AVISO_PERIODOS_SOBREPOSTOS,
  periodosStatusTemSobreposicao,
  tipoStatusProgramadoDominanteNoDia,
  ymdReferenciaAgendaStatusLocal,
} from '../lib/statusProgramadoUtils';
import { getApiBase } from '../lib/api';
import { pushAdminSyncToApiDeferred } from '../lib/adminSyncApi';
import { compressImageFileForStorage } from '../lib/imageProcessing';
import { uploadAdminMedia } from '../lib/mediaUploadApi';
import { resolveMediaUrl } from '../../../shared/lib/mediaUrl';
import { ImageWithMediaFallback } from '../components/ImageWithMediaFallback';

type ViewMode = 'list' | 'form';

/** Filtro da coluna TIPO (rascunho / publicada / lixeira). */
type FiltroTipoNoticia = '' | 'rascunho' | 'publicada' | 'lixeira';

type FormTab = 'dados' | 'localizacao' | 'imagens' | 'status';

type FormState = {
  title: string;
  subtitle: string;
  summary: string;
  content: string;
  /** Responsáveis da matéria (persistidos em `responsibles` no artigo). */
  responsaveis: NewsResponsibleUser[];
  categoryId: string;
  subcategoryIds: string[];
  cityId: string;
  localizacaoEscopo: NewsLocalizacaoEscopo;
  localizacaoPais: string;
  localizacaoEstado: string;
  localizacaoRegiao: string;
  images: string[];
  coverImage: string;
  layoutType: NewsLayoutType;
  layoutExpiresAt: string;
  destaqueSubcategoria: boolean;
  destaqueSubcategoriaExpiresAt: string;
  destaqueNaviex: boolean;
  destaqueNaviexExpiresAt: string;
  tags: string[];
  statusPadrao: 'ativada' | 'desativada';
  /** Intervalo para status programado: se **Até** vazio ou igual a **De** = um só dia → altera status padrão permanentemente. */
  periodoSelecaoDe: string;
  periodoSelecaoAte: string;
  periodosStatus: { de: string; ate: string; tipo: 'ativada' | 'desativada' | 'naviex' | 'principal' | 'categoria' }[];
  statusProgramadoHistorico: {
    quando: string;
    dia: string;
    tipo: 'ativada' | 'desativada' | 'naviex' | 'principal' | 'categoria';
    modo?: 'periodo' | 'permanente';
    ate?: string;
  }[];
};

type StatusProgramadoTipo = 'ativada' | 'desativada' | 'naviex' | 'principal' | 'categoria';

type ListaStatusDropdownState = { articleId: string; top: number; left: number };

/** Máximo de fotos por notícia (galeria). Enviadas para S3; só a referência fica no cadastro (como em experiências). */
const MAX_IMAGENS_NOTICIA = 8;

/** Caixa lateral (mesmo padrão de Experiências / Estabelecimentos). */
function InformacoesSidebarMeta({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-md border border-gray-200 bg-white shadow-sm overflow-hidden">
      <div className="px-3 py-2 border-b border-gray-200 bg-gray-50">
        <span className="text-xs font-semibold tracking-wide text-gray-800">{title}</span>
      </div>
      <div className="p-3">{children}</div>
    </div>
  );
}

function responsiblesPayloadFromFormRows(rows: NewsResponsibleUser[]): NewsResponsibleUser[] {
  return rows.map(({ userId, name, email, phone }) => {
    const o: NewsResponsibleUser = { userId, name, email };
    if (phone?.trim()) o.phone = phone.trim();
    return o;
  });
}

const emptyForm: FormState = {
  title: '',
  subtitle: '',
  summary: '',
  content: '',
  responsaveis: [],
  categoryId: '',
  subcategoryIds: [],
  cityId: '',
  localizacaoEscopo: 'global',
  localizacaoPais: '',
  localizacaoEstado: '',
  localizacaoRegiao: '',
  images: [],
  coverImage: '',
  layoutType: 'normal',
  layoutExpiresAt: '',
  destaqueSubcategoria: false,
  destaqueSubcategoriaExpiresAt: '',
  destaqueNaviex: false,
  destaqueNaviexExpiresAt: '',
  tags: [],
  statusPadrao: 'ativada',
  periodoSelecaoDe: '',
  periodoSelecaoAte: '',
  periodosStatus: [],
  statusProgramadoHistorico: [],
};

/** Título do cabeçalho do formulário: atualiza em tempo real com o campo Título. */
function tituloCabecalhoFormularioNoticia(form: FormState, isNovoArtigo: boolean): string {
  const t = form.title.trim();
  if (t) return t;
  return isNovoArtigo ? 'Novo artigo' : 'Editar artigo';
}

function inferLocalizacaoEscopoFromArticle(article: NewsArticle): NewsLocalizacaoEscopo {
  if (article.localizacaoEscopo) return article.localizacaoEscopo;
  if (article.cityId) return 'cidade';
  if (article.localizacaoRegiao?.trim()) return 'regiao';
  if (article.localizacaoEstado?.trim()) return 'estado';
  if (article.localizacaoPais?.trim()) return 'pais';
  return 'global';
}

function buildPublicacaoIssues(
  form: FormState,
  opts: { categoriesCount: number; subcategoriesForSelectedCategoryCount: number }
): string[] {
  const issues: string[] = [];
  if (!form.title.trim()) issues.push('título');
  if (!form.subtitle.trim()) issues.push('subtítulo');
  if (!form.summary.trim()) issues.push('resumo');
  if (!form.content.trim()) issues.push('conteúdo');
  if (opts.categoriesCount > 0 && !form.categoryId.trim()) issues.push('categoria');
  if (
    opts.categoriesCount > 0 &&
    form.categoryId.trim() &&
    opts.subcategoriesForSelectedCategoryCount > 0 &&
    form.subcategoryIds.length === 0
  ) {
    issues.push('subcategoria');
  }
  if (form.images.length === 0) issues.push('pelo menos uma imagem');
  if (form.images.length > 0 && (!form.coverImage || !form.images.includes(form.coverImage))) {
    issues.push('imagem de capa entre as imagens enviadas');
  }
  return issues;
}

function localizacaoStorableFromForm(form: FormState): Pick<
  NewsArticle,
  'localizacaoEscopo' | 'localizacaoPais' | 'localizacaoEstado' | 'localizacaoRegiao' | 'cityId'
> {
  const esc = form.localizacaoEscopo;
  const semLocal =
    !form.cityId &&
    !form.localizacaoPais.trim() &&
    !form.localizacaoEstado.trim() &&
    !form.localizacaoRegiao.trim();
  if (esc === 'global' || semLocal) {
    return {
      localizacaoEscopo: 'global',
      localizacaoPais: undefined,
      localizacaoEstado: undefined,
      localizacaoRegiao: undefined,
      cityId: '',
    };
  }
  if (esc === 'pais') {
    return {
      localizacaoEscopo: 'pais',
      localizacaoPais: form.localizacaoPais.trim() || undefined,
      localizacaoEstado: undefined,
      localizacaoRegiao: undefined,
      cityId: '',
    };
  }
  if (esc === 'estado') {
    return {
      localizacaoEscopo: 'estado',
      localizacaoPais: form.localizacaoPais.trim() || undefined,
      localizacaoEstado: form.localizacaoEstado.trim() || undefined,
      localizacaoRegiao: undefined,
      cityId: '',
    };
  }
  if (esc === 'regiao') {
    return {
      localizacaoEscopo: 'regiao',
      localizacaoPais: form.localizacaoPais.trim() || undefined,
      localizacaoEstado: form.localizacaoEstado.trim() || undefined,
      localizacaoRegiao: form.localizacaoRegiao.trim() || undefined,
      cityId: '',
    };
  }
  return {
    localizacaoEscopo: 'global',
    localizacaoPais: form.localizacaoPais.trim() || undefined,
    localizacaoEstado: form.localizacaoEstado.trim() || undefined,
    localizacaoRegiao: form.localizacaoRegiao.trim() || undefined,
    cityId: form.cityId,
  };
}

function formatArtigoLocalizacao(article: NewsArticle, cidades: Cidade[]): string {
  const esc = inferLocalizacaoEscopoFromArticle(article);
  if (esc === 'global') return 'Global';
  if (esc === 'pais') return article.localizacaoPais?.trim() || '—';
  if (esc === 'estado')
    return [article.localizacaoEstado, article.localizacaoPais].filter(Boolean).join(' — ') || '—';
  if (esc === 'regiao')
    return [article.localizacaoRegiao, article.localizacaoEstado, article.localizacaoPais].filter(Boolean).join(' — ') || '—';
  const c = cidades.find((x) => x.id === article.cityId);
  return c ? [c.cidade, c.estadoProvincia].filter(Boolean).join(', ') : '—';
}

function locSearchLabelFromForm(f: FormState, cit: Cidade[]): string {
  switch (f.localizacaoEscopo) {
    case 'global':
      return '';
    case 'pais':
      return f.localizacaoPais.trim();
    case 'estado':
      return [f.localizacaoEstado, f.localizacaoPais].filter(Boolean).join(', ');
    case 'regiao':
      return [f.localizacaoRegiao, f.localizacaoEstado, f.localizacaoPais].filter(Boolean).join(' — ');
    case 'cidade': {
      const c = cit.find((x) => x.id === f.cityId);
      return c ? `${c.cidade}${c.estadoProvincia ? `, ${c.estadoProvincia}` : ''}` : '';
    }
    default:
      return '';
  }
}

function centroMapaNoticia(form: FormState, cidades: Cidade[]): { lat: number; lng: number; zoom: number } | null {
  if (form.localizacaoEscopo === 'global') return { lat: 15, lng: 0, zoom: 2 };
  const match = (() => {
    switch (form.localizacaoEscopo) {
      case 'pais': {
        const p = form.localizacaoPais.trim();
        return cidades.filter((c) => (c.pais || '').trim() === p);
      }
      case 'estado':
        return cidades.filter(
          (c) =>
            (c.pais || '').trim() === form.localizacaoPais.trim() &&
            (c.estadoProvincia || '').trim() === form.localizacaoEstado.trim()
        );
      case 'regiao': {
        const r = form.localizacaoRegiao.trim();
        return cidades.filter(
          (c) =>
            (c.pais || '').trim() === form.localizacaoPais.trim() &&
            (c.estadoProvincia || '').trim() === form.localizacaoEstado.trim() &&
            (c.regioes || []).some((rr) => (rr || '').trim() === r)
        );
      }
      case 'cidade':
      default: {
        const one = cidades.find((x) => x.id === form.cityId);
        return one ? [one] : [];
      }
    }
  })();
  const list = match.filter((c) => Number.isFinite(c.lat) && Number.isFinite(c.lng));
  if (list.length === 0) return null;
  let lat = 0;
  let lng = 0;
  for (const c of list) {
    lat += c.lat as number;
    lng += c.lng as number;
  }
  lat /= list.length;
  lng /= list.length;
  const zoom =
    form.localizacaoEscopo === 'cidade' ? 12 : form.localizacaoEscopo === 'regiao' ? 9 : form.localizacaoEscopo === 'estado' ? 7 : 5;
  return { lat, lng, zoom };
}

/** Opções unificadas do combobox de status (visibilidade + tipo de destaque). */
type ModoStatusOpcao = 'ativada' | 'principal' | 'naviex' | 'categoria' | 'desativar';

const STATUS_MODO_OPTIONS: { id: ModoStatusOpcao; label: string }[] = [
  { id: 'ativada', label: 'Ativada' },
  { id: 'naviex', label: 'Destaque Naviex' },
  { id: 'principal', label: 'Destaque Principal' },
  { id: 'categoria', label: 'Destaque Categoria' },
  { id: 'desativar', label: 'Desativado' },
];

/** Largura de referência do menu da listagem: label mais longa + 8px de cada lado. */
const STATUS_MODO_LISTA_DROPDOWN_CLASS =
  'fixed z-[200] w-max rounded-lg border border-gray-200 bg-white shadow-xl overflow-hidden px-2 py-1';

function sortNewsArticlesByUpdatedDesc(list: NewsArticle[]): NewsArticle[] {
  return [...list].sort((a, b) =>
    (b.updatedAt || b.createdAt || '').localeCompare(a.updatedAt || a.createdAt || '')
  );
}

function getModoStatusFromForm(form: FormState): ModoStatusOpcao {
  if (form.statusPadrao === 'desativada') return 'desativar';
  if (form.destaqueNaviex) return 'naviex';
  if (form.layoutType === 'matéria_principal' || form.layoutType === 'slide_principal') return 'principal';
  if (form.destaqueSubcategoria) return 'categoria';
  return 'ativada';
}

/** Modo exibido no combobox: períodos em `periodosStatus` que cobrem o dia; em conflito, hierarquia de tipos. */
function getModoStatusEfetivoFromForm(form: FormState, diaYmd: string): ModoStatusOpcao {
  const tipo = tipoStatusProgramadoDominanteNoDia(form.periodosStatus, diaYmd);
  if (!tipo) return getModoStatusFromForm(form);
  switch (tipo) {
    case 'desativada':
      return 'desativar';
    case 'ativada':
      return 'ativada';
    case 'naviex':
      return 'naviex';
    case 'principal':
      return 'principal';
    case 'categoria':
      return 'categoria';
    default:
      return getModoStatusFromForm(form);
  }
}

function getModoStatusFromArticleCore(
  article: Pick<NewsArticle, 'statusPadrao' | 'layoutType' | 'destaqueSubcategoria' | 'destaqueNaviex'>
): ModoStatusOpcao {
  if (article.statusPadrao === 'desativada') return 'desativar';
  if (article.destaqueNaviex) return 'naviex';
  const lt = article.layoutType ?? 'normal';
  if (lt === 'matéria_principal' || lt === 'slide_principal') return 'principal';
  if (article.destaqueSubcategoria) return 'categoria';
  return 'ativada';
}

/** Mesma lógica do combobox de status do formulário, para listagem e filtros. */
function getModoStatusEfetivoFromArticle(article: NewsArticle, diaYmd: string): ModoStatusOpcao {
  const tipo = tipoStatusProgramadoDominanteNoDia(article.periodosStatus, diaYmd);
  if (!tipo) return getModoStatusFromArticleCore(article);
  switch (tipo) {
    case 'desativada':
      return 'desativar';
    case 'ativada':
      return 'ativada';
    case 'naviex':
      return 'naviex';
    case 'principal':
      return 'principal';
    case 'categoria':
      return 'categoria';
    default:
      return getModoStatusFromArticleCore(article);
  }
}

/** Tipo de publicação na listagem (rascunho / publicada / lixeira). */
function getNoticiaTipoPublicacao(article: NewsArticle): 'rascunho' | 'publicada' | 'lixeira' {
  const p = article.publicacaoStatus ?? 'publicado';
  if (p === 'lixeira') return 'lixeira';
  if (p === 'rascunho') return 'rascunho';
  return 'publicada';
}

function tipoNoticiaBadgeClass(tipo: 'rascunho' | 'publicada' | 'lixeira'): string {
  const base = 'inline-flex px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-wide border shrink-0';
  if (tipo === 'rascunho') return `${base} bg-amber-100 text-amber-900 border-amber-200`;
  if (tipo === 'lixeira') return `${base} bg-slate-200 text-slate-800 border-slate-300`;
  return `${base} bg-sky-100 text-sky-900 border-sky-200`;
}

function labelTipoPublicacaoColuna(tipo: 'rascunho' | 'publicada' | 'lixeira'): string {
  if (tipo === 'rascunho') return 'Rascunho';
  if (tipo === 'lixeira') return 'Lixeira';
  return 'Publicada';
}

function labelFiltroTipoPublicacaoSelect(v: FiltroTipoNoticia): string {
  if (v === '') return 'Todos';
  if (v === 'rascunho') return 'Rascunho';
  if (v === 'publicada') return 'Publicada';
  return 'Lixeira';
}

/** Fundo igual para Todos / Rascunho / Publicada no painel do filtro. */
const CLASSE_OPCAO_FILTRO_TIPO_COMUM =
  'w-full text-left px-2 py-2.5 text-xs font-medium text-gray-800 bg-gray-50 hover:bg-gray-100 border-b border-gray-100';
const CLASSE_OPCAO_FILTRO_TIPO_LIXEIRA =
  'w-full text-left px-2 py-2.5 text-xs font-medium text-violet-900 bg-violet-50 hover:bg-violet-100 border-t border-gray-200';

function buildUpdateForModo(m: ModoStatusOpcao): Partial<FormState> {
  switch (m) {
    case 'ativada':
      return {
        statusPadrao: 'ativada',
        layoutType: 'normal',
        layoutExpiresAt: '',
        destaqueSubcategoria: false,
        destaqueSubcategoriaExpiresAt: '',
        destaqueNaviex: false,
        destaqueNaviexExpiresAt: '',
      };
    case 'desativar':
      return {
        statusPadrao: 'desativada',
        layoutType: 'normal',
        layoutExpiresAt: '',
        destaqueSubcategoria: false,
        destaqueSubcategoriaExpiresAt: '',
        destaqueNaviex: false,
        destaqueNaviexExpiresAt: '',
      };
    case 'principal':
      return {
        statusPadrao: 'ativada',
        layoutType: 'matéria_principal',
        destaqueSubcategoria: false,
        destaqueSubcategoriaExpiresAt: '',
        destaqueNaviex: false,
        destaqueNaviexExpiresAt: '',
      };
    case 'categoria':
      return {
        statusPadrao: 'ativada',
        layoutType: 'normal',
        layoutExpiresAt: '',
        destaqueSubcategoria: true,
        destaqueNaviex: false,
        destaqueNaviexExpiresAt: '',
      };
    case 'naviex':
      return {
        statusPadrao: 'ativada',
        layoutType: 'normal',
        layoutExpiresAt: '',
        destaqueSubcategoria: false,
        destaqueSubcategoriaExpiresAt: '',
        destaqueNaviex: true,
      };
  }
}

function patchArticleForModoStatus(article: NewsArticle, modo: ModoStatusOpcao): NewsArticle {
  const patch = buildUpdateForModo(modo);
  const next: NewsArticle = { ...article };
  delete next.periodosStatus;

  if (patch.statusPadrao !== undefined) next.statusPadrao = patch.statusPadrao;
  if (patch.layoutType !== undefined) next.layoutType = patch.layoutType;

  if (patch.layoutExpiresAt === '') next.layoutExpiresAt = undefined;

  if (patch.destaqueSubcategoria === false) {
    next.destaqueSubcategoria = undefined;
    next.destaqueSubcategoriaExpiresAt = undefined;
  } else if (patch.destaqueSubcategoria === true) {
    next.destaqueSubcategoria = true;
  }

  if (patch.destaqueSubcategoriaExpiresAt === '') next.destaqueSubcategoriaExpiresAt = undefined;

  if (patch.destaqueNaviex === false) {
    next.destaqueNaviex = undefined;
    next.destaqueNaviexExpiresAt = undefined;
  } else if (patch.destaqueNaviex === true) {
    next.destaqueNaviex = true;
  }

  if (patch.destaqueNaviexExpiresAt === '') next.destaqueNaviexExpiresAt = undefined;

  return next;
}

function statusModoButtonClass(m: ModoStatusOpcao): string {
  const base =
    'inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border-2 transition-all min-w-[11rem] max-w-[16rem] justify-between';
  switch (m) {
    case 'desativar':
      return `${base} bg-red-500/15 border-red-500 text-red-800`;
    case 'ativada':
      return `${base} bg-emerald-500/15 border-emerald-500 text-emerald-800`;
    default:
      return `${base} bg-amber-500/12 border-amber-500 text-amber-900`;
  }
}

/** Combobox compacto na coluna Status da listagem. */
function statusModoListaButtonClass(m: ModoStatusOpcao): string {
  const base =
    'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border-2 max-w-[min(14rem,100%)] min-w-[9.5rem] justify-between transition-all hover:brightness-95';
  switch (m) {
    case 'desativar':
      return `${base} bg-red-500/15 border-red-500 text-red-800`;
    case 'ativada':
      return `${base} bg-emerald-500/15 border-emerald-500 text-emerald-800`;
    default:
      return `${base} bg-amber-500/12 border-amber-500 text-amber-900`;
  }
}

function statusModoDotClass(m: ModoStatusOpcao): string {
  switch (m) {
    case 'desativar':
      return 'bg-red-500';
    case 'ativada':
      return 'bg-emerald-500';
    case 'principal':
      return 'bg-amber-500';
    case 'naviex':
      return 'bg-violet-500';
    case 'categoria':
      return 'bg-sky-600';
  }
}

function formStateFromNewsArticle(article: NewsArticle, cidades: Cidade[]): FormState {
  const escopo = inferLocalizacaoEscopoFromArticle(article);
  const city = article.cityId ? cidades.find((c) => c.id === article.cityId) : undefined;
  return {
    title: article.title,
    subtitle: article.subtitle,
    summary: article.summary,
    content: article.content,
    responsaveis: (article.responsibles ?? []).map((r) => ({
      userId: r.userId,
      name: r.name,
      email: r.email,
      phone: r.phone ?? getComercioProfile(r.userId)?.phone,
    })),
    categoryId: article.categoryId,
    subcategoryIds: mergeNewsArticleSubcategoryIds(article),
    cityId: article.cityId,
    localizacaoEscopo: escopo,
    localizacaoPais: article.localizacaoPais ?? city?.pais ?? '',
    localizacaoEstado: article.localizacaoEstado ?? city?.estadoProvincia ?? '',
    localizacaoRegiao: article.localizacaoRegiao ?? city?.regioes?.[0] ?? '',
    ...(() => {
      const raw = [...(article.images ?? [])];
      const cov = (article.coverImage ?? '').trim();
      if (raw.length > 0 && cov && raw.includes(cov)) {
        return {
          images: [cov, ...raw.filter((u) => u !== cov)],
          coverImage: cov,
        };
      }
      return { images: raw, coverImage: raw[0] ?? '' };
    })(),
    layoutType: article.layoutType ?? 'normal',
    layoutExpiresAt: article.layoutExpiresAt ?? '',
    destaqueSubcategoria: article.destaqueSubcategoria ?? false,
    destaqueSubcategoriaExpiresAt: article.destaqueSubcategoriaExpiresAt ?? '',
    destaqueNaviex: article.destaqueNaviex ?? false,
    destaqueNaviexExpiresAt: article.destaqueNaviexExpiresAt ?? '',
    tags: article.tags ?? [],
    statusPadrao: (article.statusPadrao as 'ativada' | 'desativada') ?? 'ativada',
    periodoSelecaoDe: '',
    periodoSelecaoAte: '',
    periodosStatus: article.periodosStatus ?? [],
    statusProgramadoHistorico: article.statusProgramadoHistorico ?? [],
  };
}

function formatDateBR(isoDate: string): string {
  const parsed = new Date(isoDate);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleDateString('pt-BR');
}

function toDateInputValue(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** ISO a partir de data (YYYY-MM-DD) + hora (HH:mm) interpretados no fuso local. */
function isoFromDateAndTimeLocal(dateStr: string, timeStr: string): string {
  const date = dateStr.trim();
  if (!date) return '';
  const raw = (timeStr?.trim() || '23:59').slice(0, 5);
  const [hh = '0', mm = '0'] = raw.split(':');
  const h = Math.min(23, Math.max(0, parseInt(hh, 10) || 0));
  const m = Math.min(59, Math.max(0, parseInt(mm, 10) || 0));
  const pad = (n: number) => String(n).padStart(2, '0');
  const combined = `${date}T${pad(h)}:${pad(m)}:00`;
  const local = new Date(combined);
  if (Number.isNaN(local.getTime())) return '';
  return local.toISOString();
}

/** Ao publicar/salvar matéria: combina a data escolhida (YYYY-MM-DD) com a hora exata do momento do salvamento (fuso local). */
function mergeValidityDateWithSaveTimeNow(dateYmd: string): string {
  const date = dateYmd.trim();
  if (!date) return '';
  const parts = date.split('-').map((p) => parseInt(p, 10));
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return '';
  const [y, mo, d] = parts;
  const now = new Date();
  const local = new Date(y, mo - 1, d, now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds());
  if (Number.isNaN(local.getTime())) return '';
  return local.toISOString();
}

/** Payload de gravação da matéria (publicar, atualizar ou forçar rascunho). */
function buildNoticiaFormCommitPayload(
  form: FormState,
  publicacaoStatus: 'rascunho' | 'publicado' | 'lixeira'
): Omit<NewsArticle, 'id' | 'createdAt' | 'updatedAt'> {
  const subIds = [...new Set(form.subcategoryIds.map((id) => id.trim()).filter(Boolean))];
  return {
    title: form.title.trim(),
    subtitle: form.subtitle.trim(),
    summary: form.summary.trim(),
    content: form.content.trim(),
    categoryId: form.categoryId,
    subcategoryIds: subIds.length > 0 ? subIds : undefined,
    subcategoryId: subIds[0] ?? '',
    ...localizacaoStorableFromForm(form),
    images: form.images,
    coverImage: form.coverImage,
    estabelecimentoId: '',
    parceiroGuiaId: '',
    parceiroAgenciaId: '',
    associacoes: undefined,
    layoutType: form.layoutType,
    layoutExpiresAt:
      (form.layoutType === 'matéria_principal' || form.layoutType === 'slide_principal') &&
      toDateInputValue(form.layoutExpiresAt)
        ? mergeValidityDateWithSaveTimeNow(toDateInputValue(form.layoutExpiresAt))
        : undefined,
    destaqueSubcategoria: form.destaqueSubcategoria || undefined,
    destaqueSubcategoriaExpiresAt:
      form.destaqueSubcategoria && toDateInputValue(form.destaqueSubcategoriaExpiresAt)
        ? mergeValidityDateWithSaveTimeNow(toDateInputValue(form.destaqueSubcategoriaExpiresAt))
        : undefined,
    destaqueNaviex: form.destaqueNaviex || undefined,
    destaqueNaviexExpiresAt:
      form.destaqueNaviex && toDateInputValue(form.destaqueNaviexExpiresAt)
        ? mergeValidityDateWithSaveTimeNow(toDateInputValue(form.destaqueNaviexExpiresAt))
        : undefined,
    tags: form.tags.length > 0 ? form.tags : undefined,
    responsibles: responsiblesPayloadFromFormRows(form.responsaveis),
    statusPadrao: form.statusPadrao,
    periodosStatus: form.periodosStatus.length > 0 ? form.periodosStatus : undefined,
    statusProgramadoHistorico: [...(form.statusProgramadoHistorico ?? [])],
    publicacaoStatus,
  };
}

/** Listagem: responsáveis (novo fluxo) + nomes de associações antigas até regravar o artigo. */
function articleAssociadosResumo(
  article: NewsArticle,
  estabelecimentos: Estabelecimento[],
  guias: Guia[],
  agencias: Agencia[],
  hospedagens: Hospedagem[],
  events: Event[]
): string {
  const nomesResp = (article.responsibles ?? []).map((r) => r.name?.trim()).filter(Boolean);
  const ass = associacoesFromArticle(article);
  const legacyParts = ass.map((a) => {
    switch (a.tipo) {
      case 'estabelecimento':
        return estabelecimentos.find((e) => e.id === a.id)?.nome?.trim() || '';
      case 'parceiro_guia':
        return guias.find((g) => g.id === a.id)?.nome?.trim() || '';
      case 'parceiro_agencia':
        return agencias.find((x) => x.id === a.id)?.razaoSocial?.trim() || '';
      case 'hospedagem':
        return hospedagens.find((h) => h.id === a.id)?.nome?.trim() || '';
      case 'evento':
        return events.find((e) => e.id === a.id)?.title?.trim() || '';
      default:
        return '';
    }
  }).filter(Boolean);
  const parts = [...nomesResp, ...legacyParts];
  return parts.length > 0 ? parts.join(' · ') : '—';
}

export default function NoticiasPage() {
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [articles, setArticles] = useState<NewsArticle[]>([]);
  const [categories, setCategories] = useState<NewsCategory[]>([]);
  const [subcategories, setSubcategories] = useState<NewsSubcategory[]>([]);
  const [cities, setCities] = useState<Cidade[]>([]);
  const [estabelecimentos, setEstabelecimentos] = useState<ReturnType<typeof getEstabelecimentos>>([]);
  const [hospedagens, setHospedagens] = useState<Hospedagem[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [guias, setGuias] = useState<Guia[]>([]);
  const [agencias, setAgencias] = useState<Agencia[]>([]);
  const [form, setForm] = useState<FormState>(emptyForm);
  /** Fluxo «Nova Notícia»: cabeçalho «Novo artigo» até haver título; distingue de editar rascunho existente. */
  const [isNovoArtigoSession, setIsNovoArtigoSession] = useState(false);
  const [query, setQuery] = useState('');
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [modalCategory, setModalCategory] = useState<{ id: string | null; name: string }>({ id: null, name: '' });
  const [subcategoryModalOpen, setSubcategoryModalOpen] = useState(false);
  const [modalSubcategory, setModalSubcategory] = useState<{ id: string | null; name: string; categoryId: string }>({
    id: null,
    name: '',
    categoryId: '',
  });
  const [categoriasManagerOpen, setCategoriasManagerOpen] = useState(false);
  const [reassignModal, setReassignModal] = useState<{
    type: 'category' | 'subcategory';
    id: string;
    name: string;
    articlesCount: number;
  } | null>(null);
  const [reassignNewCategoryId, setReassignNewCategoryId] = useState('');
  const [reassignNewSubcategoryId, setReassignNewSubcategoryId] = useState('');
  const [filterLocationQuery, setFilterLocationQuery] = useState('');
  const [filterLocationFocused, setFilterLocationFocused] = useState(false);
  const [filterTipoNoticia, setFilterTipoNoticia] = useState<FiltroTipoNoticia>('');
  const [filterTipoNoticiaMenuOpen, setFilterTipoNoticiaMenuOpen] = useState(false);
  const [filterModoStatusLista, setFilterModoStatusLista] = useState<ModoStatusOpcao | ''>('');
  const [filterCategoryId, setFilterCategoryId] = useState('');
  const [filterSubcategoryId, setFilterSubcategoryId] = useState('');
  const [cidadeSearch, setCidadeSearch] = useState('');
  const [cidadeSearchFocused, setCidadeSearchFocused] = useState(false);
  const [locSearchResults, setLocSearchResults] = useState<NoticiaLocalizacaoPick[]>([]);
  const [locSearchLoading, setLocSearchLoading] = useState(false);
  const locSelectedLabelRef = useRef('');
  const [mapsReady, setMapsReady] = useState(false);
  const [mapsError, setMapsError] = useState<string | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<{ setCenter: (c: { lat: number; lng: number }) => void; setZoom: (z: number) => void } | null>(null);
  const markerRef = useRef<{ setPosition: (c: { lat: number; lng: number }) => void } | null>(null);
  const googleMapsApiKey = (
    (import.meta as { env?: Record<string, string | undefined> }).env?.VITE_GOOGLE_MAPS_API_KEY ?? ''
  ).trim();
  const [formTab, setFormTab] = useState<FormTab>('dados');
  const [noticiaImagemUploadBusy, setNoticiaImagemUploadBusy] = useState(false);
  /** Arrasto de galeria: clone segue o ponteiro; reordenação em tempo real (como em experiências). */
  const [noticiaImagemPointerDrag, setNoticiaImagemPointerDrag] = useState<{
    src: string;
    offsetX: number;
    offsetY: number;
    floatW: number;
    floatH: number;
    x: number;
    y: number;
  } | null>(null);
  const noticiaImagemCellRefs = useRef<(HTMLDivElement | null)[]>([]);
  const noticiaImagensRef = useRef<string[]>([]);
  noticiaImagensRef.current = form.images ?? [];
  const [tagInput, setTagInput] = useState('');
  const [comercioUsers, setComercioUsers] = useState<NewsResponsibleUser[]>([]);
  const [responsavelQuery, setResponsavelQuery] = useState('');
  const [responsavelOpen, setResponsavelOpen] = useState(false);
  const [modalTagsAberto, setModalTagsAberto] = useState(false);
  const [editingTagGlobal, setEditingTagGlobal] = useState<string | null>(null);
  const [editingTagValueGlobal, setEditingTagValueGlobal] = useState('');
  const [modalTagDelete, setModalTagDelete] = useState<{ tag: string; articles: NewsArticle[] } | null>(null);
  /** Sair do formulário (rascunho) com alterações: Salvar e sair / Sair / Cancelar. */
  const [rascunhoLeaveModalOpen, setRascunhoLeaveModalOpen] = useState(false);
  const [refreshTagsKey, setRefreshTagsKey] = useState(0);
  /** Menu de status manual no cabeçalho do formulário. */
  const [statusComboContext, setStatusComboContext] = useState<'header' | null>(null);
  /** Combobox de status aberto na coluna da listagem (portal fixo). */
  const [listaStatusDropdown, setListaStatusDropdown] = useState<ListaStatusDropdownState | null>(null);
  const listaStatusDropdownRef = useRef<ListaStatusDropdownState | null>(null);
  listaStatusDropdownRef.current = listaStatusDropdown;
  /** Data civil “hoje” para não permitir seleção de dias passados no calendário. */
  const hojeCalendarioSelecao = hojeYmdLocal();
  /** Dia de referência do status programado (corte 00:10:10 local). Atualiza a cada minuto. */
  const [agendaClockTick, setAgendaClockTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setAgendaClockTick((n) => n + 1), 60_000);
    return () => window.clearInterval(id);
  }, []);
  const diaAgendaStatus = useMemo(() => ymdReferenciaAgendaStatusLocal(new Date()), [agendaClockTick]);
  const location = useLocation();
  const navigate = useNavigate();
  const isFirstFormRenderRef = useRef(true);
  const persistNewsDraftRef = useRef<(opts?: { keepOnForm?: boolean; includeStatusProgramado?: boolean }) => boolean>(
    () => false
  );
  const saveNewsPublishedRef = useRef<() => Promise<boolean>>(async () => false);
  const autoDraftTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const {
    isDirty,
    setDirty,
    setIsPublishedLeave,
    setSkipAutoFlushOnLeave,
    setFlushDraftHandler,
    setSavePublishedHandler,
    setSaveDraftHandler,
  } = useAdminUnsaved();
  const { showAlert, showConfirm } = useAdminDialog();

  const refresh = () => {
    const allCategories = getNewsCategories();
    setArticles(sortNewsArticlesByUpdatedDesc(getNewsArticles()));
    setCategories(allCategories);
    setSubcategories(getNewsSubcategories());
    setCities(getCidades());
    setEstabelecimentos(getEstabelecimentos());
    setHospedagens(getHospedagens());
    setEvents(getEvents());
    setGuias(getGuias());
    setAgencias(getAgencias());
    setComercioUsers(getComercioRestauranteUsers());
  };

  useEffect(() => {
    refresh();
  }, []);

  useRefetchWhenCatalogChanges(() => refresh());

  useEffect(() => {
    if (!statusComboContext && !listaStatusDropdown) return;
    const onMouseDown = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      if (t.closest('[data-noticias-status-combo-root]')) return;
      if (t.closest('[data-noticias-lista-status-toggle="true"]')) return;
      if (t.closest('#noticias-lista-status-dropdown-portal')) return;
      setStatusComboContext(null);
      setListaStatusDropdown(null);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [statusComboContext, listaStatusDropdown]);

  useEffect(() => {
    if (!listaStatusDropdown) return;
    const articleId = listaStatusDropdown.articleId;
    const updatePosition = () => {
      const cur = listaStatusDropdownRef.current;
      if (!cur || cur.articleId !== articleId) return;
      const el = document.querySelector(`[data-noticias-status-anchor="${articleId}"]`) as HTMLElement | null;
      if (!el) {
        setListaStatusDropdown(null);
        return;
      }
      const rect = el.getBoundingClientRect();
      setListaStatusDropdown((prev) =>
        prev && prev.articleId === articleId ? { ...prev, top: rect.bottom + 4, left: rect.left } : prev
      );
    };
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [listaStatusDropdown?.articleId]);

  useEffect(() => {
    if (!filterTipoNoticiaMenuOpen) return;
    const onMouseDown = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      if (t.closest('[data-noticias-tipo-publicacao-filter]')) return;
      setFilterTipoNoticiaMenuOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [filterTipoNoticiaMenuOpen]);

  useEffect(() => {
    const state = location.state as { fromNav?: boolean } | null;
    if (state?.fromNav && location.pathname === '/noticias') {
      setViewMode('list');
      setEditingId(null);
      setIsNovoArtigoSession(false);
      setDirty(false);
      isFirstFormRenderRef.current = true;
      navigate('/noticias', { replace: true, state: {} });
    }
  }, [location.pathname, location.state, setDirty, navigate]);

  useEffect(() => {
    if (viewMode === 'list') setIsNovoArtigoSession(false);
  }, [viewMode]);

  useEffect(() => {
    if (viewMode !== 'form') {
      isFirstFormRenderRef.current = true;
      return;
    }
    if (isFirstFormRenderRef.current) {
      isFirstFormRenderRef.current = false;
      return;
    }
    setDirty(true);
  }, [form, viewMode, setDirty]);

  const filteredArticles = useMemo(() => {
    let list = articles;
    const normalized = query.trim().toLowerCase();
    if (normalized) {
      list = list.filter(
        (item) =>
          item.title.toLowerCase().includes(normalized) ||
          item.subtitle.toLowerCase().includes(normalized) ||
          item.summary.toLowerCase().includes(normalized)
      );
    }
    const locNorm = filterLocationQuery.trim().toLowerCase();
    if (locNorm) {
      list = list.filter((item) =>
        formatArtigoLocalizacao(item, cities).toLowerCase().includes(locNorm)
      );
    }
    if (filterTipoNoticia) {
      list = list.filter((item) => getNoticiaTipoPublicacao(item) === filterTipoNoticia);
    } else {
      list = list.filter((item) => {
        const t = getNoticiaTipoPublicacao(item);
        return t === 'rascunho' || t === 'publicada';
      });
    }
    if (filterModoStatusLista) {
      list = list.filter(
        (item) => getModoStatusEfetivoFromArticle(item, diaAgendaStatus) === filterModoStatusLista
      );
    }
    if (filterCategoryId) {
      list = list.filter((item) => item.categoryId === filterCategoryId);
    }
    if (filterSubcategoryId) {
      list = list.filter((item) => mergeNewsArticleSubcategoryIds(item).includes(filterSubcategoryId));
    }
    const orderIndex = new Map(articles.map((item, index) => [item.id, index]));
    return [...list].sort((a, b) => (orderIndex.get(a.id) ?? 0) - (orderIndex.get(b.id) ?? 0));
  }, [
    articles,
    query,
    filterLocationQuery,
    filterTipoNoticia,
    filterModoStatusLista,
    filterCategoryId,
    filterSubcategoryId,
    cities,
    diaAgendaStatus,
  ]);

  const subcategoriasParaFiltroLista = useMemo(
    () =>
      filterCategoryId.trim()
        ? subcategories.filter((s) => s.categoryId === filterCategoryId)
        : subcategories,
    [filterCategoryId, subcategories]
  );

  const noticiasLocacoesUnicas = useMemo(() => {
    const set = new Set<string>();
    for (const a of articles) {
      const s = formatArtigoLocalizacao(a, cities).trim();
      if (s && s !== '—') set.add(s);
    }
    return [...set].sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [articles, cities]);

  const noticiasLocacoesSugestoes = useMemo(() => {
    const q = filterLocationQuery.trim().toLowerCase();
    if (!q) return noticiasLocacoesUnicas.slice(0, 80);
    return noticiasLocacoesUnicas.filter((s) => s.toLowerCase().includes(q)).slice(0, 80);
  }, [noticiasLocacoesUnicas, filterLocationQuery]);

  const subcategoriesForForm = useMemo(
    () => (form.categoryId ? getNewsSubcategoriesByCategory(form.categoryId) : []),
    [form.categoryId, subcategories, categories]
  );

  const publicacaoIssues = useMemo(
    () =>
      buildPublicacaoIssues(form, {
        categoriesCount: categories.length,
        subcategoriesForSelectedCategoryCount: subcategoriesForForm.length,
      }),
    [form, categories.length, subcategoriesForForm]
  );

  /** Matéria já gravada como publicada (não confundir com rascunho no formulário). */
  const materiaJaPublicadaForm = useMemo(() => {
    if (!editingId) return false;
    return (getNewsArticleById(editingId)?.publicacaoStatus ?? 'publicado') === 'publicado';
  }, [editingId, articles]);

  const textoBotaoPrincipalForm = useMemo(() => {
    if (publicacaoIssues.length > 0) return 'Salvar Rascunho';
    if (materiaJaPublicadaForm) return 'Atualizar';
    return 'Publicar';
  }, [publicacaoIssues, materiaJaPublicadaForm]);

  const mostrarBotaoForcarRascunho = useMemo(
    () => textoBotaoPrincipalForm === 'Publicar' || textoBotaoPrincipalForm === 'Atualizar',
    [textoBotaoPrincipalForm]
  );

  const mapCenterNoticia = useMemo(() => centroMapaNoticia(form, cities), [form, cities]);

  useEffect(() => {
    if (!googleMapsApiKey) {
      setMapsReady(false);
      return;
    }
    const w = window as unknown as { google?: { maps?: { Map?: unknown } } };
    if (w.google?.maps?.Map) {
      setMapsReady(true);
      setMapsError(null);
      return;
    }
    const scriptId = 'google-maps-api-script-naviex';
    const existing = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', () => setMapsReady(true), { once: true });
      existing.addEventListener('error', () => setMapsError('Não foi possível carregar o Google Maps.'), { once: true });
      return;
    }
    const script = document.createElement('script');
    script.id = scriptId;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(googleMapsApiKey)}`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      setMapsReady(true);
      setMapsError(null);
    };
    script.onerror = () => setMapsError('Não foi possível carregar o Google Maps.');
    document.head.appendChild(script);
  }, [googleMapsApiKey]);

  useEffect(() => {
    if (formTab !== 'localizacao') {
      mapRef.current = null;
      markerRef.current = null;
      return;
    }
    if (!mapsReady || !mapCenterNoticia || !mapContainerRef.current) return;
    type LatLng = { lat: number; lng: number };
    const googleMaps = (
      window as unknown as {
        google?: {
          maps?: {
            Map: new (el: HTMLElement, opts: Record<string, unknown>) => {
              setCenter: (c: LatLng) => void;
              setZoom: (z: number) => void;
            };
            Marker: new (opts: Record<string, unknown>) => { setPosition: (c: LatLng) => void };
          };
        };
      }
    ).google?.maps;
    if (!googleMaps?.Map || !googleMaps?.Marker) return;
    const pos = { lat: mapCenterNoticia.lat, lng: mapCenterNoticia.lng };
    if (!mapRef.current) {
      const map = new googleMaps.Map(mapContainerRef.current, {
        center: pos,
        zoom: mapCenterNoticia.zoom,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
      });
      const marker = new googleMaps.Marker({ map, position: pos });
      mapRef.current = map;
      markerRef.current = marker;
      return;
    }
    mapRef.current.setCenter(pos);
    mapRef.current.setZoom(mapCenterNoticia.zoom);
    markerRef.current?.setPosition(pos);
  }, [formTab, mapsReady, mapCenterNoticia]);

  /** Tags disponíveis: cadastro global de notícias + tags já usadas em alguma matéria. */
  const tagsDisponiveis = useMemo(() => {
    void refreshTagsKey;
    const fromRegistry = getNewsTagRegistry();
    const fromArticles = new Set<string>();
    articles.forEach((a) =>
      (a.tags ?? []).forEach((t) => {
        const x = t.trim();
        if (x) fromArticles.add(x);
      })
    );
    return [...new Set([...fromRegistry, ...fromArticles])].sort((a, b) => a.localeCompare(b));
  }, [refreshTagsKey, articles]);

  const filteredUsersResponsavelNoticia = useMemo(() => {
    const q = responsavelQuery.trim().toLowerCase();
    const pool = comercioUsers.filter((u) => !form.responsaveis.some((r) => r.userId === u.userId));
    if (!q) return pool.slice(0, 10);
    return pool
      .filter((u) => u.name.toLowerCase().includes(q) || (u.email ?? '').toLowerCase().includes(q))
      .slice(0, 10);
  }, [comercioUsers, form.responsaveis, responsavelQuery]);

  useEffect(() => {
    if (formTab !== 'localizacao') return;
    const q = cidadeSearch.trim();
    if (!q) {
      setLocSearchResults([]);
      setLocSearchLoading(false);
      return;
    }
    const ac = new AbortController();
    setLocSearchLoading(true);
    const timer = window.setTimeout(() => {
      void searchNoticiaLocalizacoes(q, ac.signal)
        .then((rows) => {
          if (!ac.signal.aborted) setLocSearchResults(rows);
        })
        .catch(() => {
          if (!ac.signal.aborted) setLocSearchResults(buildNoticiaLocalizacaoPicks(cities, q));
        })
        .finally(() => {
          if (!ac.signal.aborted) setLocSearchLoading(false);
        });
    }, 280);
    return () => {
      window.clearTimeout(timer);
      ac.abort();
    };
  }, [cidadeSearch, formTab, cities]);

  const updateForm = (partial: Partial<FormState>) => {
    setForm((prev) => ({ ...prev, ...partial }));
  };

  const addResponsavelNoticia = (user: NewsResponsibleUser) => {
    setForm((f) => {
      if (f.responsaveis.some((r) => r.userId === user.userId)) return f;
      const profile = getComercioProfile(user.userId);
      const row: NewsResponsibleUser = {
        userId: user.userId,
        name: user.name,
        email: user.email,
        phone: profile?.phone ?? user.phone,
      };
      return { ...f, responsaveis: [...f.responsaveis, row] };
    });
    setResponsavelQuery('');
    setResponsavelOpen(false);
    setDirty(true);
  };

  const removeResponsavelNoticia = (userId: string) => {
    setForm((f) => ({
      ...f,
      responsaveis: f.responsaveis.filter((r) => r.userId !== userId),
    }));
    setDirty(true);
  };

  const modoStatusAtual = useMemo(() => getModoStatusEfetivoFromForm(form, diaAgendaStatus), [form, diaAgendaStatus]);

  const tituloCabecalhoForm = useMemo(
    () => tituloCabecalhoFormularioNoticia(form, isNovoArtigoSession),
    [form.title, isNovoArtigoSession]
  );

  const openNew = () => {
    const draft = addNewsArticle({
      title: '',
      subtitle: '',
      summary: '',
      content: '',
      categoryId: '',
      subcategoryIds: [],
      cityId: '',
      localizacaoEscopo: 'global',
      localizacaoPais: '',
      localizacaoEstado: '',
      localizacaoRegiao: '',
      images: [],
      coverImage: '',
      responsibles: [],
      statusPadrao: 'ativada',
      periodosStatus: undefined,
      statusProgramadoHistorico: [],
      publicacaoStatus: 'rascunho',
    });
    refresh();
    const loaded = getNewsArticleById(draft.id);
    if (!loaded) return;
    setEditingId(loaded.id);
    setIsNovoArtigoSession(true);
    setForm({ ...emptyForm });
    setTagInput('');
    setResponsavelQuery('');
    setResponsavelOpen(false);
    setCidadeSearch('');
    locSelectedLabelRef.current = '';
    setFormTab('dados');
    setDirty(false);
    isFirstFormRenderRef.current = true;
    setViewMode('form');
  };

  const limparFiltrosLista = () => {
    setQuery('');
    setFilterLocationQuery('');
    setFilterLocationFocused(false);
    setFilterTipoNoticia('');
    setFilterTipoNoticiaMenuOpen(false);
    setFilterModoStatusLista('');
    setFilterCategoryId('');
    setFilterSubcategoryId('');
  };

  const openEdit = (articleId: string) => {
    const article = getNewsArticleById(articleId);
    if (!article) return;
    setIsNovoArtigoSession(false);
    setEditingId(article.id);
    setTagInput('');
    setResponsavelQuery('');
    setResponsavelOpen(false);
    const cit = getCidades();
    const nextForm = formStateFromNewsArticle(article, cit);
    setForm(nextForm);
    const locLabel = locSearchLabelFromForm(nextForm, cit);
    setCidadeSearch(locLabel);
    locSelectedLabelRef.current = locLabel;
    setDirty(false);
    isFirstFormRenderRef.current = true;
    setViewMode('form');
  };

  const handleCancel = () => {
    if (viewMode !== 'form') return;
    if (!isDirty) {
      setViewMode('list');
      setEditingId(null);
      return;
    }
    const art = editingId ? getNewsArticleById(editingId) : null;
    if (art?.publicacaoStatus === 'rascunho') {
      setRascunhoLeaveModalOpen(true);
      return;
    }
    setDirty(false);
    setViewMode('list');
    setEditingId(null);
  };

  const validateForm = async (): Promise<boolean> => {
    const issues = buildPublicacaoIssues(form, {
      categoriesCount: categories.length,
      subcategoriesForSelectedCategoryCount: subcategoriesForForm.length,
    });
    if (issues.length) {
      await showAlert(
        'Não é possível publicar com campos obrigatórios incompletos:\n\n• ' + issues.join('\n• ')
      );
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    const issues = buildPublicacaoIssues(form, {
      categoriesCount: categories.length,
      subcategoriesForSelectedCategoryCount: subcategoriesForForm.length,
    });
    const publicacaoStatus = issues.length > 0 ? ('rascunho' as const) : ('publicado' as const);
    if (periodosStatusTemSobreposicao(form.periodosStatus ?? [])) {
      const ok = await showConfirm(MSG_AVISO_PERIODOS_SOBREPOSTOS, {
        title: 'Períodos sobrepostos',
        confirmText: 'Continuar',
        cancelText: 'Cancelar',
      });
      if (!ok) return;
    }

    const payload = buildNoticiaFormCommitPayload(form, publicacaoStatus);

    if (editingId) {
      const current = getNewsArticleById(editingId);
      if (!current) {
        await showAlert('Não foi possível atualizar. A notícia não foi encontrada.');
        return;
      }
      saveNewsArticle({ ...current, ...payload });
    } else {
      addNewsArticle(payload);
    }

    if (issues.length) {
      await showAlert(
        'A notícia foi salva como rascunho até que todos os campos obrigatórios estejam preenchidos:\n\n• ' +
          issues.join('\n• ')
      );
    }

    setDirty(false);
    refresh();
    setViewMode('list');
    setEditingId(null);
  };

  const handleForcarRascunho = async () => {
    if (!editingId) {
      await showAlert('Esta opção só está disponível depois de a notícia existir no sistema (edite um artigo guardado).', 'Aviso');
      return;
    }
    const current = getNewsArticleById(editingId);
    if (!current) {
      await showAlert('Não foi possível encontrar a notícia.', 'Erro');
      return;
    }
    const pub = current.publicacaoStatus ?? 'publicado';
    if (pub !== 'publicado') {
      await showAlert('Só é possível voltar a rascunho uma matéria que esteja publicada.', 'Aviso');
      return;
    }
    const ok = await showConfirm(
      'A matéria volta a ficar em rascunho e deixa de contar como publicada até publicar de novo. Deseja continuar?',
      {
        title: 'Guardar como rascunho',
        confirmText: 'Sim, guardar rascunho',
        cancelText: 'Cancelar',
      }
    );
    if (!ok) return;
    if (periodosStatusTemSobreposicao(form.periodosStatus ?? [])) {
      const okPeriodos = await showConfirm(MSG_AVISO_PERIODOS_SOBREPOSTOS, {
        title: 'Períodos sobrepostos',
        confirmText: 'Continuar',
        cancelText: 'Cancelar',
      });
      if (!okPeriodos) return;
    }
    const payload = buildNoticiaFormCommitPayload(form, 'rascunho');
    saveNewsArticle({ ...current, ...payload });
    await showAlert('Matéria guardada como rascunho.');
    setDirty(false);
    refresh();
    setViewMode('list');
    setEditingId(null);
  };

  const persistNewsDraftSilent = (opts?: { keepOnForm?: boolean; includeStatusProgramado?: boolean }): boolean => {
    const incluirSp = opts?.includeStatusProgramado === true;
    const current = editingId ? getNewsArticleById(editingId) : null;
    const periodosStatusPersist = incluirSp
      ? form.periodosStatus.length > 0
        ? form.periodosStatus
        : undefined
      : current?.periodosStatus && current.periodosStatus.length > 0
        ? current.periodosStatus
        : undefined;
    const statusProgramadoHistoricoPersist = incluirSp
      ? [...(form.statusProgramadoHistorico ?? [])]
      : [...(current?.statusProgramadoHistorico ?? [])];

    const payload: Omit<NewsArticle, 'id' | 'createdAt' | 'updatedAt' | 'publicacaoStatus'> = {
      title: form.title.trim(),
      subtitle: form.subtitle.trim(),
      summary: form.summary.trim(),
      content: form.content.trim(),
      categoryId: form.categoryId || '',
      subcategoryIds: [...new Set(form.subcategoryIds.map((id) => id.trim()).filter(Boolean))],
      subcategoryId: (form.subcategoryIds.find((id) => id.trim()) ?? '').trim(),
      ...localizacaoStorableFromForm(form),
      images: form.images,
      coverImage: form.coverImage ?? '',
      estabelecimentoId: '',
      parceiroGuiaId: '',
      parceiroAgenciaId: '',
      associacoes: undefined,
      layoutType: form.layoutType,
      layoutExpiresAt:
        (form.layoutType === 'matéria_principal' || form.layoutType === 'slide_principal') &&
        toDateInputValue(form.layoutExpiresAt)
          ? isoFromDateAndTimeLocal(toDateInputValue(form.layoutExpiresAt), '00:00')
          : undefined,
      destaqueSubcategoria: form.destaqueSubcategoria || undefined,
      destaqueSubcategoriaExpiresAt:
        form.destaqueSubcategoria && toDateInputValue(form.destaqueSubcategoriaExpiresAt)
          ? isoFromDateAndTimeLocal(toDateInputValue(form.destaqueSubcategoriaExpiresAt), '00:00')
          : undefined,
      destaqueNaviex: form.destaqueNaviex || undefined,
      destaqueNaviexExpiresAt:
        form.destaqueNaviex && toDateInputValue(form.destaqueNaviexExpiresAt)
          ? isoFromDateAndTimeLocal(toDateInputValue(form.destaqueNaviexExpiresAt), '00:00')
          : undefined,
      tags: form.tags.length > 0 ? form.tags : undefined,
      responsibles: responsiblesPayloadFromFormRows(form.responsaveis),
      statusPadrao: form.statusPadrao,
      periodosStatus: periodosStatusPersist,
      statusProgramadoHistorico: statusProgramadoHistoricoPersist,
    };
    let articleIdAfterSave = editingId;
    try {
      if (editingId) {
        const current = getNewsArticleById(editingId);
        if (!current) return false;
        const publicacaoStatus =
          current.publicacaoStatus === 'publicado'
            ? 'publicado'
            : current.publicacaoStatus === 'lixeira'
              ? 'lixeira'
              : 'rascunho';
        saveNewsArticle({ ...current, ...payload, publicacaoStatus });
      } else {
        const added = addNewsArticle({ ...payload, publicacaoStatus: 'rascunho' });
        articleIdAfterSave = added.id;
        if (opts?.keepOnForm) setEditingId(added.id);
      }
    } catch {
      return false;
    }
    if (incluirSp) {
      setDirty(false);
    } else if (articleIdAfterSave) {
      const r = getNewsArticleById(articleIdAfterSave);
      const pDiff = JSON.stringify(form.periodosStatus ?? []) !== JSON.stringify(r?.periodosStatus ?? []);
      const hDiff =
        JSON.stringify(form.statusProgramadoHistorico ?? []) !== JSON.stringify(r?.statusProgramadoHistorico ?? []);
      if (pDiff || hDiff) setDirty(true);
      else setDirty(false);
    } else {
      setDirty(false);
    }
    refresh();
    if (!opts?.keepOnForm) {
      setViewMode('list');
      setEditingId(null);
    }
    return true;
  };

  const savePublishedNewsForLeave = async (): Promise<boolean> => {
    const valid = await validateForm();
    if (!valid) return false;
    if (periodosStatusTemSobreposicao(form.periodosStatus ?? [])) {
      const ok = await showConfirm(MSG_AVISO_PERIODOS_SOBREPOSTOS, {
        title: 'Períodos sobrepostos',
        confirmText: 'Continuar',
        cancelText: 'Cancelar',
      });
      if (!ok) return false;
    }
    const subIdsPub = [...new Set(form.subcategoryIds.map((id) => id.trim()).filter(Boolean))];
    const payload = {
      title: form.title.trim(),
      subtitle: form.subtitle.trim(),
      summary: form.summary.trim(),
      content: form.content.trim(),
      categoryId: form.categoryId,
      subcategoryIds: subIdsPub.length > 0 ? subIdsPub : undefined,
      subcategoryId: subIdsPub[0] ?? '',
      ...localizacaoStorableFromForm(form),
      images: form.images,
      coverImage: form.coverImage,
      estabelecimentoId: '',
      parceiroGuiaId: '',
      parceiroAgenciaId: '',
      associacoes: undefined,
      layoutType: form.layoutType,
      layoutExpiresAt:
        (form.layoutType === 'matéria_principal' || form.layoutType === 'slide_principal') &&
        toDateInputValue(form.layoutExpiresAt)
          ? mergeValidityDateWithSaveTimeNow(toDateInputValue(form.layoutExpiresAt))
          : undefined,
      destaqueSubcategoria: form.destaqueSubcategoria || undefined,
      destaqueSubcategoriaExpiresAt:
        form.destaqueSubcategoria && toDateInputValue(form.destaqueSubcategoriaExpiresAt)
          ? mergeValidityDateWithSaveTimeNow(toDateInputValue(form.destaqueSubcategoriaExpiresAt))
          : undefined,
      destaqueNaviex: form.destaqueNaviex || undefined,
      destaqueNaviexExpiresAt:
        form.destaqueNaviex && toDateInputValue(form.destaqueNaviexExpiresAt)
          ? mergeValidityDateWithSaveTimeNow(toDateInputValue(form.destaqueNaviexExpiresAt))
          : undefined,
      tags: form.tags.length > 0 ? form.tags : undefined,
      responsibles: responsiblesPayloadFromFormRows(form.responsaveis),
      statusPadrao: form.statusPadrao,
      periodosStatus: form.periodosStatus.length > 0 ? form.periodosStatus : undefined,
      statusProgramadoHistorico: [...(form.statusProgramadoHistorico ?? [])],
      publicacaoStatus: 'publicado' as const,
    };
    if (editingId) {
      const current = getNewsArticleById(editingId);
      if (!current) {
        await showAlert('Não foi possível atualizar. A notícia não foi encontrada.');
        return false;
      }
      saveNewsArticle({ ...current, ...payload });
    } else {
      addNewsArticle(payload);
    }
    setDirty(false);
    refresh();
    setViewMode('list');
    setEditingId(null);
    return true;
  };

  persistNewsDraftRef.current = persistNewsDraftSilent;
  saveNewsPublishedRef.current = savePublishedNewsForLeave;

  useEffect(() => {
    if (viewMode !== 'form') {
      setIsPublishedLeave(false);
      setSkipAutoFlushOnLeave(false);
      setFlushDraftHandler(null);
      setSavePublishedHandler(null);
      setSaveDraftHandler(null);
      return;
    }
    const storedPub = editingId ? (getNewsArticleById(editingId)?.publicacaoStatus ?? 'publicado') : 'rascunho';
    const isRascunho = storedPub === 'rascunho';
    setIsPublishedLeave(Boolean(isDirty && storedPub === 'publicado'));
    setSkipAutoFlushOnLeave(isRascunho);
    /** Rascunho: não gravar ao sair pela sidebar (usa modal Salvar e sair / Sair / Cancelar). */
    setFlushDraftHandler(null);
    setSaveDraftHandler(
      isRascunho && isDirty
        ? async () => Promise.resolve(persistNewsDraftRef.current({ keepOnForm: false, includeStatusProgramado: true }))
        : null
    );
    setSavePublishedHandler(
      storedPub === 'publicado' && isDirty ? async () => saveNewsPublishedRef.current() : null
    );
    return () => {
      setSkipAutoFlushOnLeave(false);
      setFlushDraftHandler(null);
      setSavePublishedHandler(null);
      setSaveDraftHandler(null);
    };
  }, [
    viewMode,
    editingId,
    isDirty,
    setIsPublishedLeave,
    setSkipAutoFlushOnLeave,
    setFlushDraftHandler,
    setSavePublishedHandler,
    setSaveDraftHandler,
  ]);

  useEffect(() => {
    if (viewMode !== 'form') {
      if (autoDraftTimerRef.current) {
        clearTimeout(autoDraftTimerRef.current);
        autoDraftTimerRef.current = null;
      }
      return;
    }
    const storedPub = editingId ? (getNewsArticleById(editingId)?.publicacaoStatus ?? 'publicado') : 'rascunho';
    if (storedPub !== 'rascunho') {
      if (autoDraftTimerRef.current) {
        clearTimeout(autoDraftTimerRef.current);
        autoDraftTimerRef.current = null;
      }
      return;
    }
    if (isFirstFormRenderRef.current) return;
    if (!isDirty) return;
    if (autoDraftTimerRef.current) clearTimeout(autoDraftTimerRef.current);
    autoDraftTimerRef.current = setTimeout(() => {
      autoDraftTimerRef.current = null;
      void persistNewsDraftRef.current({ keepOnForm: true, includeStatusProgramado: false });
    }, 1200);
    return () => {
      if (autoDraftTimerRef.current) {
        clearTimeout(autoDraftTimerRef.current);
        autoDraftTimerRef.current = null;
      }
    };
  }, [form, viewMode, editingId, isDirty]);

  /** Ao fechar aba/navegador: grava rascunho em silêncio; matéria publicada com alterações não salvas avisa. */
  useEffect(() => {
    if (viewMode !== 'form') return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!isDirty) return;
      const art = editingId ? getNewsArticleById(editingId) : null;
      if (art?.publicacaoStatus === 'publicado') {
        e.preventDefault();
        e.returnValue = '';
        return;
      }
      void persistNewsDraftRef.current({ keepOnForm: true, includeStatusProgramado: false });
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [viewMode, isDirty, editingId, form]);

  const handleDelete = async (article: NewsArticle) => {
    if (article.publicacaoStatus === 'lixeira') {
      const confirmed = await showConfirm(
        `Apagar permanentemente a notícia "${article.title}"? Esta ação não pode ser desfeita.`,
        {
          title: 'Apagar da lixeira',
          confirmText: 'Apagar para sempre',
          cancelText: 'Cancelar',
        }
      );
      if (!confirmed) return;
      removeNewsArticle(article.id);
      refresh();
      return;
    }
    const confirmed = await showConfirm(
      `Mover a notícia "${article.title}" para a lixeira? Poderá filtrá-la em TIPO e restaurar ao editar e publicar de novo.`,
      {
        title: 'Mover para a lixeira',
        confirmText: 'Mover para lixeira',
        cancelText: 'Cancelar',
      }
    );
    if (!confirmed) return;
    saveNewsArticle({ ...article, publicacaoStatus: 'lixeira' });
    refresh();
  };

  const handleNoticiaImageFiles = (files: FileList | null) => {
    if (!files?.length) return;
    const allowed = ['image/png', 'image/jpeg', 'image/jpg'];
    const toProcess = Array.from(files).filter((file) => allowed.includes(file.type));
    if (toProcess.length === 0) {
      void showAlert('Use apenas PNG ou JPG.', 'Aviso');
      return;
    }
    const current = form.images?.length ?? 0;
    const slots = MAX_IMAGENS_NOTICIA - current;
    if (slots <= 0) {
      void showAlert(`Limite de ${MAX_IMAGENS_NOTICIA} fotos. Remova alguma antes de adicionar outras.`, 'Aviso');
      return;
    }
    const batch = toProcess.slice(0, slots);
    if (toProcess.length > slots) {
      void showAlert(`Só é possível adicionar mais ${slots} foto(s). As primeiras ${slots} serão enviadas.`, 'Aviso');
    }
    void (async () => {
      setNoticiaImagemUploadBusy(true);
      const refs: string[] = [];
      try {
        for (const file of batch) {
          const dataUrl = await compressImageFileForStorage(file, { maxSide: 1920, quality: 0.82 });
          const blob = await (await fetch(dataUrl)).blob();
          const jpegFile = new File([blob], 'noticia.jpg', { type: blob.type || 'image/jpeg' });
          const { reference } = await uploadAdminMedia(jpegFile, 'noticias');
          refs.push(reference);
        }
        setForm((f) => {
          const images = [...(f.images ?? []), ...refs];
          const coverImage = images[0] ?? '';
          return { ...f, images, coverImage };
        });
        setDirty(true);
      } catch (e) {
        await showAlert(
          e instanceof Error ? e.message : 'Não foi possível enviar as imagens. Verifique se a API está no ar e o bucket S3 configurado.',
          'Erro ao enviar'
        );
      } finally {
        setNoticiaImagemUploadBusy(false);
      }
    })();
  };

  const removeNoticiaImagem = (index: number) => {
    setForm((f) => {
      const images = f.images.filter((_, i) => i !== index);
      const coverImage = images[0] ?? '';
      return { ...f, images, coverImage };
    });
    setDirty(true);
  };

  const moveNoticiaImagemParaIndice = (fromIndex: number, toIndex: number, opts?: { markDirty?: boolean }) => {
    if (fromIndex === toIndex) return;
    setForm((f) => {
      const list = [...(f.images ?? [])];
      if (fromIndex < 0 || fromIndex >= list.length || toIndex < 0 || toIndex >= list.length) return f;
      const [item] = list.splice(fromIndex, 1);
      list.splice(toIndex, 0, item);
      noticiaImagensRef.current = list;
      const coverImage = list[0] ?? '';
      return { ...f, images: list, coverImage };
    });
    if (opts?.markDirty !== false) {
      setDirty(true);
    }
  };

  const getNoticiaImagemCellIndexAtPoint = (clientX: number, clientY: number): number | null => {
    const refs = noticiaImagemCellRefs.current;
    for (let i = 0; i < refs.length; i++) {
      const el = refs[i];
      if (!el) continue;
      const r = el.getBoundingClientRect();
      if (clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom) return i;
    }
    return null;
  };

  /** Arrasto só com rato (mousedown + window mousemove/mouseup). */
  const handleNoticiaImagemCellMouseDown = (e: React.MouseEvent<HTMLDivElement>, src: string) => {
    if (noticiaImagemUploadBusy) return;
    if ((e.target as HTMLElement).closest('button')) return;
    if (e.button !== 0) return;
    e.preventDefault();
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    setNoticiaImagemPointerDrag({
      src,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
      floatW: rect.width,
      floatH: rect.height,
      x: e.clientX,
      y: e.clientY,
    });
    const dragSrc = src;

    const prevCursor = typeof document !== 'undefined' ? document.body.style.cursor : '';
    const prevUserSelect = typeof document !== 'undefined' ? document.body.style.userSelect : '';
    if (typeof document !== 'undefined') {
      document.body.style.cursor = 'grabbing';
      document.body.style.userSelect = 'none';
    }

    const onMove = (ev: MouseEvent) => {
      if (!card.isConnected) return;
      ev.preventDefault();
      setNoticiaImagemPointerDrag((prev) => (prev ? { ...prev, x: ev.clientX, y: ev.clientY } : null));
      const hoverIdx = getNoticiaImagemCellIndexAtPoint(ev.clientX, ev.clientY);
      if (hoverIdx === null) return;
      const list = noticiaImagensRef.current;
      const fromIdx = list.indexOf(dragSrc);
      if (fromIdx === -1 || fromIdx === hoverIdx) return;
      moveNoticiaImagemParaIndice(fromIdx, hoverIdx, { markDirty: false });
    };

    const onUp = () => {
      window.removeEventListener('mousemove', onMove, true);
      window.removeEventListener('mouseup', onUp, true);
      if (typeof document !== 'undefined') {
        document.body.style.cursor = prevCursor;
        document.body.style.userSelect = prevUserSelect;
      }
      setNoticiaImagemPointerDrag(null);
      setDirty(true);
    };

    window.addEventListener('mousemove', onMove, true);
    window.addEventListener('mouseup', onUp, true);
  };

  const aplicarLocalizacaoPick = (pick: NoticiaLocalizacaoPick) => {
    updateForm(formPatchFromNoticiaLocalizacaoPick(pick));
    locSelectedLabelRef.current = pick.label;
    setCidadeSearch(pick.label);
    setCidadeSearchFocused(false);
    setDirty(true);
  };

  const labelTipoProgramadoNoticia = (tipo: string) => {
    switch (tipo) {
      case 'ativada':
        return 'Ativar';
      case 'desativada':
        return 'Desativado';
      case 'naviex':
        return 'Destaque Naviex';
      case 'principal':
        return 'Destaque Principal';
      case 'categoria':
        return 'Destaque Categoria';
      default:
        return tipo;
    }
  };

  const adicionarPeriodoStatus = (tipo: StatusProgramadoTipo) => {
    let de = form.periodoSelecaoDe?.trim() || '';
    let ate = form.periodoSelecaoAte?.trim() || '';
    if (!de) return;
    if (!ate) ate = de;
    if (de > ate) [de, ate] = [ate, de];

    const umSoDia = de === ate;
    const quando = new Date().toISOString();

    if (umSoDia) {
      if (tipo === 'ativada' || tipo === 'desativada') {
        updateForm({
          statusPadrao: tipo === 'ativada' ? 'ativada' : 'desativada',
          periodoSelecaoDe: '',
          periodoSelecaoAte: '',
          statusProgramadoHistorico: [
            ...(form.statusProgramadoHistorico ?? []),
            { quando, dia: de, tipo, modo: 'permanente' },
          ],
        });
        return;
      }
      const modoKey = tipo === 'naviex' ? 'naviex' : tipo === 'principal' ? 'principal' : 'categoria';
      updateForm({
        ...buildUpdateForModo(modoKey),
        periodoSelecaoDe: '',
        periodoSelecaoAte: '',
        statusProgramadoHistorico: [
          ...(form.statusProgramadoHistorico ?? []),
          { quando, dia: de, tipo, modo: 'permanente' },
        ],
      });
      return;
    }

    updateForm({
      periodosStatus: [...(form.periodosStatus ?? []), { de, ate, tipo }].sort((a, b) => a.de.localeCompare(b.de)),
      periodoSelecaoDe: '',
      periodoSelecaoAte: '',
    });
  };

  const removerPeriodoStatus = (index: number) => {
    updateForm({ periodosStatus: (form.periodosStatus ?? []).filter((_, i) => i !== index) });
  };

  const openModalCategory = (cat?: NewsCategory) => {
    setModalCategory(cat ? { id: cat.id, name: cat.name } : { id: null, name: '' });
    setCategoryModalOpen(true);
  };

  const openModalSubcategory = (sub?: NewsSubcategory) => {
    setModalSubcategory(
      sub
        ? { id: sub.id, name: sub.name, categoryId: sub.categoryId }
        : { id: null, name: '', categoryId: form.categoryId || categories[0]?.id || '' }
    );
    setSubcategoryModalOpen(true);
  };

  const saveModalCategory = async () => {
    const name = modalCategory.name.trim();
    if (!name) {
      await showAlert('Informe o nome da categoria.');
      return;
    }
    try {
      if (modalCategory.id) {
        updateNewsCategory(modalCategory.id, name);
      } else {
        const created = addNewsCategory(name);
        updateForm({ categoryId: created.id, subcategoryIds: [] });
      }
      setCategoryModalOpen(false);
      setModalCategory({ id: null, name: '' });
      refresh();
      if (categoriasManagerOpen) setCategoriasManagerOpen(true);
    } catch (error) {
      await showAlert(error instanceof Error ? error.message : 'Não foi possível salvar a categoria.');
    }
  };

  const saveModalSubcategory = async () => {
    const name = modalSubcategory.name.trim();
    if (!modalSubcategory.categoryId) {
      await showAlert('Selecione a categoria da subcategoria.');
      return;
    }
    if (!name) {
      await showAlert('Informe o nome da subcategoria.');
      return;
    }
    try {
      if (modalSubcategory.id) {
        updateNewsSubcategory(modalSubcategory.id, name, modalSubcategory.categoryId);
      } else {
        const created = addNewsSubcategory(modalSubcategory.categoryId, name);
        setForm((prev) => ({
          ...prev,
          categoryId: created.categoryId,
          subcategoryIds:
            prev.categoryId === created.categoryId
              ? [...new Set([...prev.subcategoryIds, created.id])]
              : [created.id],
        }));
        setDirty(true);
      }
      setSubcategoryModalOpen(false);
      setModalSubcategory({ id: null, name: '', categoryId: '' });
      refresh();
      if (categoriasManagerOpen) setCategoriasManagerOpen(true);
    } catch (error) {
      await showAlert(error instanceof Error ? error.message : 'Não foi possível salvar a subcategoria.');
    }
  };

  const handleDeleteCategory = async (cat: NewsCategory) => {
    const result = await removeNewsCategory(cat.id);
    if (result.ok) {
      refresh();
      setCategoriasManagerOpen(false);
      setCategoryModalOpen(false);
      if (form.categoryId === cat.id) {
        updateForm({ categoryId: '', subcategoryIds: [] });
      }
      await showAlert(`Categoria "${cat.name}" excluída com sucesso.`, 'Sucesso');
      return;
    }
    setReassignModal({
      type: 'category',
      id: cat.id,
      name: cat.name,
      articlesCount: result.articlesCount,
    });
    setReassignNewCategoryId('');
    setReassignNewSubcategoryId('');
  };

  const handleDeleteSubcategory = async (sub: NewsSubcategory) => {
    const result = await removeNewsSubcategory(sub.id);
    if (result.ok) {
      refresh();
      setCategoriasManagerOpen(false);
      setSubcategoryModalOpen(false);
      if (form.subcategoryIds.includes(sub.id)) {
        setForm((prev) => ({
          ...prev,
          subcategoryIds: prev.subcategoryIds.filter((id) => id !== sub.id),
        }));
        setDirty(true);
      }
      await showAlert(`Subcategoria "${sub.name}" excluída com sucesso.`, 'Sucesso');
      return;
    }
    setReassignModal({
      type: 'subcategory',
      id: sub.id,
      name: sub.name,
      articlesCount: result.articlesCount,
    });
    setReassignNewCategoryId('');
    setReassignNewSubcategoryId('');
  };

  const confirmReassignAndDelete = async () => {
    if (!reassignModal) return;
    if (!reassignNewCategoryId || !reassignNewSubcategoryId) {
      await showAlert('Selecione a nova categoria e subcategoria para as matérias afetadas.');
      return;
    }
    const subsForCat = getNewsSubcategoriesByCategory(reassignNewCategoryId);
    if (!subsForCat.some((s) => s.id === reassignNewSubcategoryId)) {
      await showAlert('A subcategoria selecionada não pertence à categoria escolhida.');
      return;
    }
    try {
      if (reassignModal.type === 'category') {
        await removeNewsCategory(reassignModal.id, {
          newCategoryId: reassignNewCategoryId,
          newSubcategoryId: reassignNewSubcategoryId,
        });
      } else {
        await removeNewsSubcategory(reassignModal.id, {
          newCategoryId: reassignNewCategoryId,
          newSubcategoryId: reassignNewSubcategoryId,
        });
      }
      refresh();
      setReassignModal(null);
      setCategoriasManagerOpen(false);
      setCategoryModalOpen(false);
      setSubcategoryModalOpen(false);
      await showAlert(
        `Matérias reatribuídas e ${reassignModal.type === 'category' ? 'categoria' : 'subcategoria'} excluída com sucesso.`,
        'Sucesso'
      );
    } catch (error) {
      await showAlert(error instanceof Error ? error.message : 'Erro ao excluir.');
    }
  };

  const categoryNameById = (id: string): string =>
    categories.find((category) => category.id === id)?.name ?? '—';
  const subcategoryNameById = (id: string): string =>
    subcategories.find((sub) => sub.id === id)?.name ?? '—';
  const todasAsTags = tagsDisponiveis;

  const toggleNewsTag = (tag: string) => {
    setForm((f) => {
      const tags = f.tags ?? [];
      const next = tags.includes(tag) ? tags.filter((t) => t !== tag) : [...tags, tag];
      return { ...f, tags: next };
    });
    setDirty(true);
  };

  const addNewsTagFromInput = (raw: string) => {
    const t = raw.trim();
    if (!t) return;
    addNewsTagToRegistry(t);
    setRefreshTagsKey((k) => k + 1);
    setForm((f) => {
      const tags = f.tags ?? [];
      if (tags.includes(t)) return f;
      return { ...f, tags: [...tags, t] };
    });
    setTagInput('');
    setDirty(true);
  };

  const saveRenameNewsTagGlobal = (oldTag: string, newTag: string) => {
    const t = newTag.trim();
    if (!t || t === oldTag) {
      setEditingTagGlobal(null);
      setEditingTagValueGlobal('');
      return;
    }
    renameNewsTagInRegistry(oldTag, t);
    setRefreshTagsKey((k) => k + 1);
    refresh();
    setForm((f) => ({
      ...f,
      tags: (f.tags ?? []).map((tag) => (tag === oldTag ? t : tag)),
    }));
    setEditingTagGlobal(null);
    setEditingTagValueGlobal('');
  };

  const deleteNewsTagGlobal = (tag: string) => {
    const arts = getNewsArticles().filter((a) => (a.tags ?? []).includes(tag));
    if (arts.length === 0) {
      removeNewsTagFromRegistryOnly(tag);
      setRefreshTagsKey((k) => k + 1);
      return;
    }
    setModalTagDelete({ tag, articles: arts });
  };

  const confirmDeleteNewsTag = () => {
    if (!modalTagDelete) return;
    const t = modalTagDelete.tag;
    removeNewsTagCompletely(t);
    setModalTagDelete(null);
    setRefreshTagsKey((k) => k + 1);
    refresh();
    setForm((f) => ({ ...f, tags: (f.tags ?? []).filter((x) => x !== t) }));
  };

  const applyModoStatusFromLista = (articleId: string, modo: ModoStatusOpcao) => {
    const current = getNewsArticleById(articleId);
    if (!current) {
      void showAlert('Notícia não encontrada para atualizar o status.');
      return;
    }
    try {
      const saved = saveNewsArticle(patchArticleForModoStatus(current, modo));
      setArticles((prev) => prev.map((item) => (item.id === articleId ? saved : item)));
      setListaStatusDropdown(null);
      pushAdminSyncToApiDeferred(async (syncResult) => {
        if (!syncResult.ok) {
          await showAlert(
            `Status guardado localmente, mas a sincronização com a API falhou: ${syncResult.error ?? 'erro desconhecido'}.`,
            'Atenção'
          );
        }
      });
    } catch (error) {
      void showAlert(error instanceof Error ? error.message : 'Não foi possível salvar o status.');
    }
  };

  const listaStatusDropdownArticle = listaStatusDropdown
    ? articles.find((item) => item.id === listaStatusDropdown.articleId) ?? null
    : null;
  const listaStatusDropdownModo =
    listaStatusDropdownArticle && getNoticiaTipoPublicacao(listaStatusDropdownArticle) !== 'lixeira'
      ? getModoStatusEfetivoFromArticle(listaStatusDropdownArticle, diaAgendaStatus)
      : null;

  const IconPencil = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
    </svg>
  );
  const ManualStatusCombo = () => (
    <div className="relative shrink-0" data-noticias-status-combo-root>
      <button
        type="button"
        onClick={() => setStatusComboContext((c) => (c === 'header' ? null : 'header'))}
        className={statusModoButtonClass(modoStatusAtual)}
      >
        <span className="inline-flex items-center gap-2 min-w-0">
          <span className={`relative flex h-2 w-2 shrink-0 rounded-full ${statusModoDotClass(modoStatusAtual)}`} />
          <span className="truncate text-left">
            {STATUS_MODO_OPTIONS.find((o) => o.id === modoStatusAtual)?.label ?? 'Status'}
          </span>
        </span>
        <svg
          className={`w-4 h-4 shrink-0 transition-transform ${statusComboContext === 'header' ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {statusComboContext === 'header' && (
        <div className="absolute top-full left-0 mt-1 py-1 rounded-lg border-2 border-gray-200 bg-white shadow-lg z-10 min-w-[15rem] max-w-[min(100vw-2rem,20rem)]">
          {STATUS_MODO_OPTIONS.map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => {
                updateForm(buildUpdateForModo(opt.id));
                setStatusComboContext(null);
              }}
              className={`w-full inline-flex items-center gap-2 px-4 py-2.5 text-left text-sm font-medium ${
                opt.id === modoStatusAtual ? 'bg-orange-50/80' : 'hover:bg-gray-50'
              }`}
            >
              <span className={`rounded-full h-2 w-2 shrink-0 ${statusModoDotClass(opt.id)}`} />
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  const IconTrash = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
      />
    </svg>
  );

  return (
    <AdminLayout>
      <div className="w-full">
        <div className="sticky top-0 z-20 px-4 sm:px-6 py-6 border-b border-gray-200 bg-white shadow-sm">
          <nav className="text-sm text-gray-500 mb-1">
            <Link to="/dashboard" className="hover:text-gray-700">
              Home
            </Link>
            {' > '}
            <Link to="/noticias" className="hover:text-gray-700">
              Notícias
            </Link>
            {viewMode === 'form' && (
              <>
                {' '}
                {' > '}
                <span className="text-gray-800">{tituloCabecalhoForm}</span>
              </>
            )}
          </nav>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 break-words min-w-0">
                {viewMode === 'list' ? 'Dashboard de Notícias' : tituloCabecalhoForm}
              </h1>
              {viewMode === 'form' && <ManualStatusCombo />}
            </div>
            {viewMode === 'list' ? (
              <div className="flex flex-wrap items-center justify-end gap-2 shrink-0">
                <button
                  type="button"
                  onClick={openNew}
                  className="px-4 py-2 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600"
                >
                  Nova Notícia
                </button>
              </div>
            ) : (
              <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3 shrink-0 min-w-0">
                {mostrarBotaoForcarRascunho && (
                  <button
                    type="button"
                    onClick={() => void handleForcarRascunho()}
                    className="px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base font-medium bg-orange-500 text-white hover:bg-orange-600 whitespace-nowrap shrink-0"
                  >
                    Rascunho
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-3 sm:px-4 py-2 border border-gray-300 rounded-lg text-sm sm:text-base font-medium text-gray-700 hover:bg-gray-50 whitespace-nowrap shrink-0"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={() => void handleSave()}
                  className={`px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base font-medium whitespace-nowrap shrink-0 ${
                    publicacaoIssues.length > 0
                      ? 'bg-orange-500 text-white hover:bg-orange-600'
                      : 'bg-emerald-600 text-white hover:bg-emerald-700'
                  }`}
                >
                  {textoBotaoPrincipalForm}
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="px-4 sm:px-6 pt-0 pb-6">
          {viewMode === 'list' ? (
            <>
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[1100px] text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th scope="col" className="p-2 align-bottom font-normal min-w-[6.5rem] text-center">
                          <span className="sr-only">Filtrar por tipo de publicação</span>
                          <div className="relative w-full min-w-0" data-noticias-tipo-publicacao-filter>
                            <button
                              type="button"
                              id="noticias-filtro-tipo-publicacao"
                              aria-haspopup="listbox"
                              aria-expanded={filterTipoNoticiaMenuOpen}
                              onClick={() => setFilterTipoNoticiaMenuOpen((o) => !o)}
                              className="w-full min-w-0 text-xs py-2 px-2 rounded-md border border-gray-300 bg-white text-gray-800 shadow-sm inline-flex items-center justify-between gap-1"
                            >
                              <span className="truncate">{labelFiltroTipoPublicacaoSelect(filterTipoNoticia)}</span>
                              <svg
                                className={`w-3.5 h-3.5 shrink-0 text-gray-500 transition-transform ${
                                  filterTipoNoticiaMenuOpen ? 'rotate-180' : ''
                                }`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                aria-hidden
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                            {filterTipoNoticiaMenuOpen ? (
                              <ul
                                className="absolute z-50 left-0 right-0 mt-1 min-w-[8rem] rounded-md border border-gray-200 bg-white shadow-lg overflow-hidden text-left"
                                role="listbox"
                                aria-labelledby="noticias-filtro-tipo-publicacao"
                              >
                                {(
                                  [
                                    { v: '' as const, lixeira: false },
                                    { v: 'rascunho' as const, lixeira: false },
                                    { v: 'publicada' as const, lixeira: false },
                                    { v: 'lixeira' as const, lixeira: true },
                                  ] as const
                                ).map(({ v, lixeira }) => {
                                  const sel = filterTipoNoticia === v;
                                  return (
                                    <li key={v || 'todos'} role="option" aria-selected={sel}>
                                      <button
                                        type="button"
                                        className={`${lixeira ? CLASSE_OPCAO_FILTRO_TIPO_LIXEIRA : CLASSE_OPCAO_FILTRO_TIPO_COMUM} ${
                                          sel && !lixeira ? 'bg-orange-50/90 ring-1 ring-inset ring-orange-200/80' : ''
                                        } ${sel && lixeira ? 'bg-violet-100 ring-1 ring-inset ring-violet-200' : ''}`}
                                        onMouseDown={(e) => {
                                          e.preventDefault();
                                          setFilterTipoNoticia(v);
                                          setFilterTipoNoticiaMenuOpen(false);
                                        }}
                                      >
                                        {labelFiltroTipoPublicacaoSelect(v)}
                                      </button>
                                    </li>
                                  );
                                })}
                              </ul>
                            ) : null}
                          </div>
                        </th>
                        <th scope="col" className="p-2 align-bottom font-normal min-w-[11rem] max-w-[16rem]">
                          <label className="sr-only">Filtrar por título, subtítulo ou resumo</label>
                          <input
                            type="text"
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                            placeholder="Título, subtítulo…"
                            className="w-full min-w-0 text-xs py-2 px-2 rounded-md border border-gray-300 bg-white text-gray-800 shadow-sm"
                          />
                        </th>
                        <th scope="col" className="p-2 align-bottom font-normal min-w-[7.5rem]">
                          <label className="sr-only">Filtrar por categoria</label>
                          <select
                            value={filterCategoryId}
                            onChange={(e) => {
                              const v = e.target.value;
                              setFilterCategoryId(v);
                              setFilterSubcategoryId((sid) => {
                                if (!v) return sid;
                                const sub = subcategories.find((s) => s.id === sid);
                                return sub && sub.categoryId === v ? sid : '';
                              });
                            }}
                            className="w-full min-w-0 text-xs py-2 px-2 rounded-md border border-gray-300 bg-white text-gray-800 shadow-sm"
                          >
                            <option value="">Todas</option>
                            {categories.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.name}
                              </option>
                            ))}
                          </select>
                        </th>
                        <th scope="col" className="p-2 align-bottom font-normal min-w-[7.5rem]">
                          <label className="sr-only">Filtrar por subcategoria</label>
                          <select
                            value={filterSubcategoryId}
                            onChange={(e) => setFilterSubcategoryId(e.target.value)}
                            className="w-full min-w-0 text-xs py-2 px-2 rounded-md border border-gray-300 bg-white text-gray-800 shadow-sm"
                          >
                            <option value="">Todas</option>
                            {subcategoriasParaFiltroLista.map((s) => (
                              <option key={s.id} value={s.id}>
                                {s.name}
                              </option>
                            ))}
                          </select>
                        </th>
                        <th scope="col" className="p-2 align-bottom font-normal min-w-[9.5rem]">
                          <label className="sr-only">Filtrar por localização</label>
                          <div className="relative w-full min-w-0">
                            <input
                              type="text"
                              value={filterLocationQuery}
                              onChange={(e) => setFilterLocationQuery(e.target.value)}
                              onFocus={() => setFilterLocationFocused(true)}
                              onBlur={() => setTimeout(() => setFilterLocationFocused(false), 200)}
                              placeholder="Localização…"
                              className="w-full min-w-0 text-xs py-2 px-2 rounded-md border border-gray-300 bg-white text-gray-800 shadow-sm"
                            />
                            {filterLocationFocused ? (
                              <ul className="absolute z-40 left-0 right-0 mt-1 min-w-[12rem] max-h-52 overflow-auto bg-white border border-gray-200 rounded-md shadow-lg text-left">
                                {noticiasLocacoesSugestoes.length > 0 ? (
                                  noticiasLocacoesSugestoes.map((label) => (
                                    <li key={label}>
                                      <button
                                        type="button"
                                        className="w-full text-left px-2 py-2 text-xs text-gray-800 hover:bg-orange-50 truncate"
                                        onMouseDown={(e) => {
                                          e.preventDefault();
                                          setFilterLocationQuery(label);
                                          setFilterLocationFocused(false);
                                        }}
                                      >
                                        {label}
                                      </button>
                                    </li>
                                  ))
                                ) : (
                                  <li className="px-2 py-2 text-xs text-gray-500">
                                    {noticiasLocacoesUnicas.length === 0
                                      ? 'Sem localizações nas notícias.'
                                      : 'Nenhum resultado.'}
                                  </li>
                                )}
                              </ul>
                            ) : null}
                          </div>
                        </th>
                        <th scope="col" className="p-2 align-bottom font-normal min-w-[10rem]">
                          <span className="sr-only">Sem filtro na coluna associados</span>
                        </th>
                        <th scope="col" className="p-2 align-bottom font-normal min-w-[8rem] text-center">
                          <label className="sr-only">Filtrar por status</label>
                          <select
                            value={filterModoStatusLista}
                            onChange={(e) => setFilterModoStatusLista(e.target.value as ModoStatusOpcao | '')}
                            className="w-full min-w-0 text-xs py-2 px-2 rounded-md border border-gray-300 bg-white text-gray-800 shadow-sm"
                          >
                            <option value="">Todos</option>
                            {STATUS_MODO_OPTIONS.map((opt) => (
                              <option key={opt.id} value={opt.id}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </th>
                        <th scope="col" className="p-2 align-bottom font-normal min-w-[8.5rem]">
                          <span className="sr-only">Sem filtro na coluna atualizado</span>
                        </th>
                        <th scope="col" className="p-2 align-bottom font-normal min-w-[6.5rem] text-right">
                          <button
                            type="button"
                            onClick={limparFiltrosLista}
                            className="w-full min-w-0 text-xs py-2 px-2 rounded-md border border-gray-300 bg-white text-gray-700 font-medium shadow-sm hover:bg-sky-50 hover:border-sky-300 hover:text-sky-800"
                            title="Limpar todos os filtros"
                          >
                            Limpar
                          </button>
                          <span className="sr-only">Limpar todos os filtros da listagem</span>
                        </th>
                      </tr>
                      <tr className="bg-orange-500 text-white">
                        <th scope="col" className="text-center py-3 px-2 font-semibold">Tipo</th>
                        <th scope="col" className="text-left py-3 px-4 font-semibold">Título</th>
                        <th scope="col" className="text-left py-3 px-4 font-semibold">Categoria</th>
                        <th scope="col" className="text-left py-3 px-4 font-semibold">Subcategoria</th>
                        <th scope="col" className="text-left py-3 px-4 font-semibold">Localização</th>
                        <th scope="col" className="text-left py-3 px-4 font-semibold">Responsáveis</th>
                        <th scope="col" className="text-center py-3 px-2 font-semibold">Status</th>
                        <th scope="col" className="text-left py-3 px-4 font-semibold">Atualizado</th>
                        <th scope="col" className="text-right py-3 px-4 font-semibold min-w-[6.5rem] whitespace-nowrap">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredArticles.map((article, idx) => {
                        const tipoPub = getNoticiaTipoPublicacao(article);
                        const modoLista =
                          tipoPub === 'lixeira'
                            ? null
                            : getModoStatusEfetivoFromArticle(article, diaAgendaStatus);
                        return (
                        <tr key={article.id} className={`${['bg-white', 'bg-gray-50', 'bg-orange-50'][idx % 3]} border-b border-gray-100 hover:opacity-90 transition-opacity`}>
                          <td className="text-center py-3 px-2">
                            <span className={tipoNoticiaBadgeClass(tipoPub)}>{labelTipoPublicacaoColuna(tipoPub)}</span>
                          </td>
                          <td className="py-3 px-4">
                            <div className="font-medium text-gray-900">
                              {article.title.trim()
                                ? article.title
                                : (
                                  <span className="text-gray-400 font-normal italic">Sem título</span>
                                )}
                            </div>
                            {article.subtitle.trim() ? (
                              <div className="text-xs text-gray-500">{article.subtitle}</div>
                            ) : null}
                          </td>
                          <td className="py-3 px-4">{categoryNameById(article.categoryId)}</td>
                          <td className="py-3 px-4">
                            {mergeNewsArticleSubcategoryIds(article)
                              .map((id) => subcategoryNameById(id))
                              .filter((n) => n && n !== '—')
                              .join(', ') || '—'}
                          </td>
                          <td className="py-3 px-4">{formatArtigoLocalizacao(article, cities)}</td>
                          <td className="py-3 px-4 text-xs text-gray-700 max-w-[20rem] leading-snug">
                            {articleAssociadosResumo(article, estabelecimentos, guias, agencias, hospedagens, events)}
                          </td>
                          <td className="text-center py-3 px-2">
                            {modoLista ? (
                              <div
                                className="relative inline-block mx-auto"
                                data-noticias-lista-status-toggle="true"
                                data-noticias-status-anchor={article.id}
                              >
                                <button
                                  type="button"
                                  onClick={(event) => {
                                    const rect = event.currentTarget.getBoundingClientRect();
                                    setListaStatusDropdown((current) =>
                                      current?.articleId === article.id
                                        ? null
                                        : { articleId: article.id, top: rect.bottom + 4, left: rect.left }
                                    );
                                  }}
                                  className={statusModoListaButtonClass(modoLista)}
                                  aria-haspopup="listbox"
                                  aria-expanded={listaStatusDropdown?.articleId === article.id}
                                  title="Alterar status"
                                >
                                  <span className="inline-flex items-center gap-1.5 min-w-0">
                                    <span
                                      className={`relative flex h-2 w-2 shrink-0 rounded-full ${statusModoDotClass(modoLista)}`}
                                    />
                                    <span className="truncate text-left">
                                      {STATUS_MODO_OPTIONS.find((o) => o.id === modoLista)?.label ?? modoLista}
                                    </span>
                                  </span>
                                  <svg
                                    className={`w-3.5 h-3.5 shrink-0 transition-transform ${
                                      listaStatusDropdown?.articleId === article.id ? 'rotate-180' : ''
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    aria-hidden
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                  </svg>
                                </button>
                              </div>
                            ) : (
                              <span className="text-gray-400 text-xs">—</span>
                            )}
                          </td>
                          <td className="py-3 px-4">{formatDateBR(article.updatedAt)}</td>
                          <td className="py-3 px-4 min-w-[6.5rem]">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => openEdit(article.id)}
                                className="p-2 rounded-lg text-gray-500 hover:text-orange-600 hover:bg-orange-50/80"
                                title="Editar"
                              >
                                <svg className="w-6 h-6 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5M16.5 3.5a2.121 2.121 0 113 3L12 14l-4 1 1-4 7.5-7.5z" />
                                </svg>
                              </button>
                              <button
                                type="button"
                                onClick={() => void handleDelete(article)}
                                className="p-2 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50/80"
                                title={article.publicacaoStatus === 'lixeira' ? 'Apagar permanentemente' : 'Mover para a lixeira'}
                              >
                                <svg className="w-6 h-6 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {filteredArticles.length === 0 && (
                  <div className="p-8 text-center text-gray-500">Nenhuma notícia cadastrada.</div>
                )}
              </div>
            </>
          ) : (
            <div className="w-full max-w-full pt-4">
              <div className="flex gap-2 mb-4 overflow-x-auto pb-2 flex-wrap">
                {[
                  { id: 'dados' as const, label: 'Dados da notícia' },
                  { id: 'localizacao' as const, label: 'Localização' },
                  { id: 'imagens' as const, label: 'Imagens' },
                  { id: 'status' as const, label: 'Status Programado' },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setFormTab(tab.id)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${formTab === tab.id ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <div className="pt-4">
              {formTab === 'dados' && (
              <section className="bg-gray-50 rounded-xl p-6 border border-gray-200 w-full">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Dados da notícia</h2>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start">
                  <div className="lg:col-span-9 space-y-4 min-w-0">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Título *</label>
                      <input
                        type="text"
                        value={form.title}
                        onChange={(event) => updateForm({ title: event.target.value })}
                        placeholder="Título da matéria"
                        className="w-full h-10 px-3 box-border border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                      />
                    </div>

                    <div className="w-full">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Subtítulo *</label>
                      <input
                        type="text"
                        value={form.subtitle}
                        onChange={(event) => updateForm({ subtitle: event.target.value })}
                        placeholder="Subtítulo da matéria"
                        className="w-full py-2 px-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                      />
                    </div>

                    <div className="w-full">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Resumo *</label>
                      <textarea
                        value={form.summary}
                        onChange={(event) => updateForm({ summary: event.target.value })}
                        placeholder="Resumo curto para listagens e vitrine"
                        rows={3}
                        className="w-full py-2 px-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-400 focus:border-transparent min-h-[88px] resize-y"
                      />
                    </div>

                    <div>
                      <div className="block text-sm font-medium text-gray-700 mb-1">Conteúdo *</div>
                      <EstabelecimentoDescricaoEditor
                        key={editingId ? `noticia-desc-${editingId}` : 'noticia-desc-novo'}
                        id="noticia-form-conteudo"
                        value={form.content ?? ''}
                        onChange={(html) => {
                          updateForm({ content: html });
                          setDirty(true);
                        }}
                        placeholder="Corpo do artigo…"
                      />
                    </div>
                  </div>

                  <aside className="lg:col-span-3 space-y-4 min-w-0">
                    <InformacoesSidebarMeta title="Categoria *">
                      <div className="flex flex-wrap items-center justify-end gap-2 mb-2">
                        <button
                          type="button"
                          onClick={() => openModalCategory()}
                          className="text-xs text-orange-600 hover:underline"
                        >
                          Nova categoria
                        </button>
                        <button
                          type="button"
                          onClick={() => setCategoriasManagerOpen(true)}
                          className="text-xs text-gray-600 hover:underline"
                        >
                          Gerenciar
                        </button>
                      </div>
                      <select
                        value={form.categoryId}
                        onChange={(event) => {
                          updateForm({
                            categoryId: event.target.value,
                            subcategoryIds: [],
                          });
                          setDirty(true);
                        }}
                        className="w-full px-2 py-2 text-sm border border-gray-300 rounded-lg"
                      >
                        <option value="">Selecione</option>
                        {categories.map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                    </InformacoesSidebarMeta>

                    <InformacoesSidebarMeta title={`Subcategorias${subcategoriesForForm.length > 0 ? ' *' : ''}`}>
                      <div className="flex flex-wrap items-center justify-end gap-2 mb-2">
                        <button
                          type="button"
                          onClick={() => openModalSubcategory()}
                          className="text-xs text-orange-600 hover:underline"
                        >
                          Nova subcategoria
                        </button>
                        <button
                          type="button"
                          onClick={() => setCategoriasManagerOpen(true)}
                          className="text-xs text-gray-600 hover:underline"
                        >
                          Gerenciar
                        </button>
                      </div>
                      {subcategoriesForForm.length === 0 && form.categoryId ? (
                        <p className="text-xs text-gray-500">Nenhuma subcategoria para esta categoria</p>
                      ) : !form.categoryId ? (
                        <p className="text-xs text-gray-500">Selecione uma categoria.</p>
                      ) : (
                        <div className="flex flex-wrap gap-1.5 rounded-lg border border-gray-200 bg-gray-50 p-2">
                          {subcategoriesForForm.map((subcategory) => (
                            <label
                              key={subcategory.id}
                              className="inline-flex items-center gap-1.5 cursor-pointer rounded-md border border-gray-200 bg-white px-2 py-1 text-xs hover:bg-orange-50 has-[:checked]:border-orange-400 has-[:checked]:bg-orange-50/60"
                            >
                              <input
                                type="checkbox"
                                className="rounded border-gray-300 text-orange-600 focus:ring-orange-500 shrink-0"
                                checked={form.subcategoryIds.includes(subcategory.id)}
                                onChange={() => {
                                  setForm((f) => {
                                    const has = f.subcategoryIds.includes(subcategory.id);
                                    const next = has
                                      ? f.subcategoryIds.filter((id) => id !== subcategory.id)
                                      : [...f.subcategoryIds, subcategory.id];
                                    return { ...f, subcategoryIds: next };
                                  });
                                  setDirty(true);
                                }}
                              />
                              <span className="leading-tight">{subcategory.name}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </InformacoesSidebarMeta>

                    <InformacoesSidebarMeta title="Responsáveis">
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {form.responsaveis.map((r) => (
                          <span
                            key={r.userId}
                            className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-50 text-orange-800 rounded text-xs"
                          >
                            {r.name}
                            {r.phone && (
                              <a
                                href={`https://wa.me/55${r.phone.replace(/\D/g, '')}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-green-600 hover:underline"
                                title="WhatsApp"
                              >
                                WA
                              </a>
                            )}
                            <button
                              type="button"
                              onClick={() => removeResponsavelNoticia(r.userId)}
                              className="text-red-500 hover:text-red-700"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                      <div className="relative">
                        <input
                          type="text"
                          value={responsavelQuery}
                          onChange={(e) => {
                            setResponsavelQuery(e.target.value);
                            setResponsavelOpen(true);
                          }}
                          onFocus={() => setResponsavelOpen(true)}
                          placeholder="Buscar usuário…"
                          className="w-full px-2 py-2 text-sm border border-gray-300 rounded-lg"
                        />
                        {responsavelOpen && (
                          <div className="absolute z-[280] left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                            {filteredUsersResponsavelNoticia.length === 0 ? (
                              <div className="px-3 py-2 text-gray-500 text-sm">Nenhum utilizador encontrado</div>
                            ) : (
                              filteredUsersResponsavelNoticia.map((u) => (
                                <button
                                  key={u.userId}
                                  type="button"
                                  onClick={() => addResponsavelNoticia(u)}
                                  className="w-full text-left px-3 py-2 hover:bg-orange-50 text-sm"
                                >
                                  {u.name} ({u.email})
                                </button>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    </InformacoesSidebarMeta>

                    <InformacoesSidebarMeta title="Tags">
                      <div className="flex justify-end mb-2">
                        <button
                          type="button"
                          onClick={() => setModalTagsAberto(true)}
                          className="text-xs text-gray-600 hover:underline"
                        >
                          Gerenciar lista
                        </button>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <input
                          type="text"
                          value={tagInput}
                          onChange={(e) => setTagInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              addNewsTagFromInput(tagInput);
                            }
                          }}
                          placeholder="Nova tag…"
                          className="flex-1 min-w-[6rem] py-1.5 px-2 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                        />
                        <button
                          type="button"
                          onClick={() => addNewsTagFromInput(tagInput)}
                          className="px-2.5 py-1.5 rounded-lg border border-orange-500 text-orange-600 text-xs font-medium hover:bg-orange-50 shrink-0"
                        >
                          Adicionar
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mb-2">
                        Lista global do admin; toque para marcar ou desmarcar na matéria.
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {tagsDisponiveis.map((tag) => {
                          const selected = (form.tags ?? []).includes(tag);
                          return (
                            <button
                              key={tag}
                              type="button"
                              onClick={() => toggleNewsTag(tag)}
                              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border text-xs font-medium transition-colors ${
                                selected
                                  ? 'bg-orange-100 border-orange-400 text-orange-800'
                                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              {tag}
                            </button>
                          );
                        })}
                      </div>
                    </InformacoesSidebarMeta>
                  </aside>
                </div>
              </section>
              )}

              {formTab === 'localizacao' && (
              <section className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <h2 className="text-lg font-semibold text-gray-800 mb-2">Localização</h2>
                <p className="text-xs text-gray-600 mb-4">
                  Campo opcional. Busque entre as cidades cadastradas (e o estado, região ou país de cada uma). Deixe em
                  branco para a notícia valer em <strong>todo o planeta (Global)</strong>.
                </p>

                <div className="relative w-full max-w-2xl">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Localização</label>
                  <input
                    type="text"
                    value={cidadeSearch}
                    onChange={(e) => {
                      const v = e.target.value;
                      setCidadeSearch(v);
                      setDirty(true);
                      if (!v.trim()) {
                        locSelectedLabelRef.current = '';
                        updateForm(EMPTY_NOTICIA_LOCALIZACAO_FORM);
                        return;
                      }
                      if (locSelectedLabelRef.current && v !== locSelectedLabelRef.current) {
                        locSelectedLabelRef.current = '';
                        updateForm(EMPTY_NOTICIA_LOCALIZACAO_FORM);
                      }
                    }}
                    onFocus={() => setCidadeSearchFocused(true)}
                    onBlur={() => setTimeout(() => setCidadeSearchFocused(false), 200)}
                    placeholder="Busque cidade, estado, região ou país… (vazio = Global)"
                    className="w-full py-2 px-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                    autoComplete="off"
                  />
                  {cidadeSearchFocused && cidadeSearch.trim() !== '' && (
                    <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-auto">
                      {locSearchLoading ? (
                        <li className="px-3 py-2 text-sm text-gray-500">A buscar…</li>
                      ) : locSearchResults.length === 0 ? (
                        <li className="px-3 py-2 text-sm text-gray-500">
                          Nenhuma localização cadastrada encontrada.
                        </li>
                      ) : (
                        locSearchResults.map((pick) => (
                          <li
                            key={
                              pick.kind === 'cidade'
                                ? `c-${pick.cityId}`
                                : pick.kind === 'pais'
                                  ? `p-${pick.pais}`
                                  : pick.kind === 'estado'
                                    ? `e-${pick.pais}-${pick.estado}`
                                    : `r-${pick.pais}-${pick.estado}-${pick.regiao}`
                            }
                            className="px-3 py-2 text-sm cursor-pointer hover:bg-orange-50 border-b border-gray-100 last:border-0"
                            onMouseDown={() => aplicarLocalizacaoPick(pick)}
                          >
                            <span className="text-[10px] font-semibold uppercase tracking-wide text-orange-700 mr-2">
                              {pick.kind === 'cidade'
                                ? 'Cidade'
                                : pick.kind === 'estado'
                                  ? 'Estado'
                                  : pick.kind === 'pais'
                                    ? 'País'
                                    : 'Região'}
                            </span>
                            <span className="text-gray-900">{pick.label}</span>
                          </li>
                        ))
                      )}
                    </ul>
                  )}
                </div>

                <p className="mt-3 text-sm text-gray-700">
                  <span className="font-medium text-gray-800">Selecionado: </span>
                  {labelLocalizacaoResumo(form.localizacaoEscopo, form, cities)}
                </p>

                <div className="mt-6 rounded-xl border border-gray-200 overflow-hidden bg-gray-100 min-h-[200px]">
                  {!googleMapsApiKey ? (
                    <p className="p-4 text-sm text-gray-600">
                      Configure <code className="text-xs bg-white px-1 rounded">VITE_GOOGLE_MAPS_API_KEY</code> para
                      exibir o mapa (mesmo padrão das outras páginas do admin).
                    </p>
                  ) : mapsError ? (
                    <p className="p-4 text-sm text-red-600">{mapsError}</p>
                  ) : !mapsReady ? (
                    <p className="p-4 text-sm text-gray-600">A carregar mapa…</p>
                  ) : (
                    <div ref={mapContainerRef} className="w-full h-[280px] bg-gray-200" />
                  )}
                </div>
              </section>
              )}

              {formTab === 'imagens' && (
              <section className="bg-gray-50 rounded-xl p-6 border border-gray-200 w-full">
                <h2 className="text-lg font-semibold text-gray-800 mb-2">Imagens</h2>
                <p className="text-xs text-gray-500 mb-4">
                  Até {MAX_IMAGENS_NOTICIA} fotos por notícia. O ficheiro é enviado para o armazenamento na nuvem (S3); aqui ficam só as referências (não base64 no navegador). Para publicar é obrigatório pelo menos uma imagem. A{' '}
                  <strong className="font-semibold text-gray-700">primeira imagem</strong> é a de capa nos cards. Com o <strong className="font-semibold text-gray-700">rato</strong>: clique, segure e arraste — a ordem atualiza ao passar sobre cada posição; a miniatura segue o movimento.
                </p>
                <div
                  className={`border-2 border-dashed border-gray-300 rounded-xl p-8 text-center bg-white transition-colors ${
                    noticiaImagemUploadBusy || (form.images?.length ?? 0) >= MAX_IMAGENS_NOTICIA
                      ? 'opacity-60 cursor-not-allowed'
                      : 'hover:border-orange-400 cursor-pointer'
                  }`}
                  onDragOver={(e) => {
                    e.preventDefault();
                    if (noticiaImagemUploadBusy || (form.images?.length ?? 0) >= MAX_IMAGENS_NOTICIA) return;
                    e.currentTarget.classList.add('border-orange-400', 'bg-orange-50/50');
                  }}
                  onDragLeave={(e) => {
                    e.currentTarget.classList.remove('border-orange-400', 'bg-orange-50/50');
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('border-orange-400', 'bg-orange-50/50');
                    if (noticiaImagemUploadBusy || (form.images?.length ?? 0) >= MAX_IMAGENS_NOTICIA) return;
                    handleNoticiaImageFiles(e.dataTransfer.files);
                  }}
                  onClick={() => {
                    if (noticiaImagemUploadBusy || (form.images?.length ?? 0) >= MAX_IMAGENS_NOTICIA) return;
                    document.getElementById('noticia-imagens-input')?.click();
                  }}
                >
                  <input
                    id="noticia-imagens-input"
                    type="file"
                    accept="image/png,image/jpeg,image/jpg"
                    multiple
                    disabled={noticiaImagemUploadBusy || (form.images?.length ?? 0) >= MAX_IMAGENS_NOTICIA}
                    className="hidden"
                    onChange={(e) => {
                      handleNoticiaImageFiles(e.target.files);
                      e.target.value = '';
                    }}
                  />
                  <svg className="w-12 h-12 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <p className="text-gray-700 font-medium">{noticiaImagemUploadBusy ? 'A enviar…' : 'Faça o upload'}</p>
                  <p className="text-sm text-gray-500 mt-1">
                    PNG ou JPG — comprimidos antes do envio para o bucket S3 (categoria notícias)
                  </p>
                </div>
                {form.images && form.images.length > 0 && (
                  <>
                    <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                      {form.images.map((src, i) => (
                        <div
                          key={src}
                          ref={(el) => {
                            noticiaImagemCellRefs.current[i] = el;
                          }}
                          onMouseDown={(e) => handleNoticiaImagemCellMouseDown(e, src)}
                          className={`relative rounded-lg overflow-hidden aspect-[4/3] bg-gray-100 select-none ${
                            !noticiaImagemUploadBusy ? 'cursor-grab' : ''
                          } ${noticiaImagemPointerDrag?.src === src ? 'opacity-0 pointer-events-none' : ''}`}
                          aria-hidden={noticiaImagemPointerDrag?.src === src}
                        >
                          <div
                            className={`relative h-full w-full ${
                              i === 0
                                ? 'ring-2 ring-orange-500 ring-offset-2 ring-offset-gray-50 border-2 border-orange-500'
                                : 'border border-gray-200'
                            }`}
                          >
                            <ImageWithMediaFallback
                              src={resolveMediaUrl(src, getApiBase())}
                              alt=""
                              draggable={false}
                              className="w-full h-full object-cover pointer-events-none"
                            />
                            {i === 0 && (
                              <span className="absolute bottom-2 left-2 px-2.5 py-1 rounded-md text-sm font-bold bg-orange-500 text-white shadow-md tracking-tight">
                                principal
                              </span>
                            )}
                          </div>
                          <button
                            type="button"
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={() => removeNoticiaImagem(i)}
                            className="absolute top-1 right-1 w-7 h-7 rounded-full bg-red-500 text-white text-sm hover:bg-red-600 z-10 shadow cursor-pointer"
                            aria-label="Remover imagem"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                    {noticiaImagemPointerDrag && (
                      <div
                        className="fixed z-[400] pointer-events-none rounded-lg overflow-hidden shadow-2xl border-2 border-orange-400 ring-2 ring-orange-300/60 bg-gray-900/10"
                        style={{
                          left: noticiaImagemPointerDrag.x - noticiaImagemPointerDrag.offsetX,
                          top: noticiaImagemPointerDrag.y - noticiaImagemPointerDrag.offsetY,
                          width: noticiaImagemPointerDrag.floatW,
                          height: noticiaImagemPointerDrag.floatH,
                        }}
                      >
                        <ImageWithMediaFallback
                          src={resolveMediaUrl(noticiaImagemPointerDrag.src, getApiBase())}
                          alt=""
                          draggable={false}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                  </>
                )}
              </section>
              )}

              {formTab === 'status' && (
                <StatusProgramadoAgendaPanel
                  minDateYmd={hojeCalendarioSelecao}
                  periodoDe={form.periodoSelecaoDe}
                  periodoAte={form.periodoSelecaoAte}
                  onPeriodoChange={(de, ate) => updateForm({ periodoSelecaoDe: de, periodoSelecaoAte: ate })}
                  periodosStatus={form.periodosStatus ?? []}
                  statusProgramadoHistorico={form.statusProgramadoHistorico ?? []}
                  diaAgendaStatus={diaAgendaStatus}
                  onRemovePeriodo={removerPeriodoStatus}
                  onAddPeriodo={(tipo) => adicionarPeriodoStatus(tipo as StatusProgramadoTipo)}
                  labelTipoProgramado={labelTipoProgramadoNoticia}
                  tipoAtivo="ativada"
                  tipoDesativado="desativada"
                />
              )}

              </div>
            </div>
          )}
        </div>
      </div>

      {modalTagsAberto && (
        <div
          className="fixed inset-0 z-[320] flex items-center justify-center p-4 bg-black/50"
          onClick={() => {
            setModalTagsAberto(false);
            setEditingTagGlobal(null);
            setEditingTagValueGlobal('');
          }}
        >
          <div
            className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[80vh] overflow-hidden flex flex-col border border-gray-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Gerenciar tags de notícias</h3>
                <button
                  type="button"
                  onClick={() => {
                    setModalTagsAberto(false);
                    setEditingTagGlobal(null);
                    setEditingTagValueGlobal('');
                  }}
                  className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                  aria-label="Fechar"
                >
                  ✕
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Edite o texto da tag ou exclua. Tags aqui não alteram as tags de experiências. Se a tag não estiver em nenhuma
                matéria, a exclusão é direta; caso contrário, será removida de todas as matérias que a usam.
              </p>
            </div>
            <ul className="p-4 overflow-y-auto flex-1">
              {todasAsTags.length === 0 ? (
                <li className="text-sm text-gray-500">Nenhuma tag cadastrada.</li>
              ) : (
                todasAsTags.map((tag) => (
                  <li key={tag} className="flex items-center gap-2 py-2 border-b border-gray-100 last:border-0">
                    {editingTagGlobal === tag ? (
                      <>
                        <input
                          type="text"
                          value={editingTagValueGlobal}
                          onChange={(e) => setEditingTagValueGlobal(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              saveRenameNewsTagGlobal(tag, editingTagValueGlobal);
                            }
                            if (e.key === 'Escape') {
                              setEditingTagGlobal(null);
                              setEditingTagValueGlobal('');
                            }
                          }}
                          className="flex-1 py-1.5 px-2 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-orange-400"
                          autoFocus
                        />
                        <button
                          type="button"
                          onClick={() => saveRenameNewsTagGlobal(tag, editingTagValueGlobal)}
                          className="p-1.5 rounded-lg text-orange-600 hover:bg-orange-50"
                          title="Salvar"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setEditingTagGlobal(null);
                            setEditingTagValueGlobal('');
                          }}
                          className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100"
                        >
                          Cancelar
                        </button>
                      </>
                    ) : (
                      <>
                        <span className="flex-1 text-sm font-medium text-gray-800">{tag}</span>
                        <button
                          type="button"
                          onClick={() => {
                            setEditingTagGlobal(tag);
                            setEditingTagValueGlobal(tag);
                          }}
                          className="p-1.5 rounded-lg text-orange-600 hover:bg-orange-50"
                          title="Editar"
                        >
                          <IconPencil />
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteNewsTagGlobal(tag)}
                          className="p-1.5 rounded-lg text-red-600 hover:bg-red-50"
                          title="Excluir"
                        >
                          <IconTrash />
                        </button>
                      </>
                    )}
                  </li>
                ))
              )}
            </ul>
          </div>
        </div>
      )}

      {modalTagDelete && (
        <div className="fixed inset-0 z-[330] flex items-center justify-center p-4 bg-black/50" onClick={() => setModalTagDelete(null)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden border border-gray-200" onClick={(e) => e.stopPropagation()}>
            <div className="bg-amber-50 border-b border-amber-200 px-6 py-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-600">
                  <IconTrash />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Excluir tag &quot;{modalTagDelete.tag}&quot;</h3>
                  <p className="text-sm text-amber-800/90 mt-0.5">Esta tag está vinculada a uma ou mais matérias.</p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <p className="text-sm text-gray-700 mb-3">
                Ao confirmar, a tag será <strong>removida do cadastro</strong> e <strong>retirada de todas as matérias</strong>{' '}
                listadas abaixo. As matérias não são excluídas.
              </p>
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 mb-4 max-h-40 overflow-y-auto">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Matérias que usam esta tag</p>
                <ul className="space-y-1.5 text-sm text-gray-800">
                  {modalTagDelete.articles.map((a) => (
                    <li key={a.id} className="flex items-center gap-2">
                      <span className="h-1.5 w-1.5 rounded-full bg-orange-400 shrink-0" />
                      {a.title}
                    </li>
                  ))}
                </ul>
              </div>
              <p className="text-sm text-gray-600 mb-4">Deseja realmente excluir esta tag?</p>
              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => setModalTagDelete(null)}
                  className="px-4 py-2.5 border border-gray-300 rounded-xl text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={confirmDeleteNewsTag}
                  className="px-4 py-2.5 bg-red-500 text-white rounded-xl font-medium hover:bg-red-600 transition-colors shadow-sm"
                >
                  Excluir tag de todas
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {rascunhoLeaveModalOpen && (
        <div
          className="fixed inset-0 z-[335] flex items-center justify-center p-4 bg-black/50"
          onClick={() => setRascunhoLeaveModalOpen(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 border border-gray-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex gap-4 mb-6">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Alterações não salvas</h3>
                <p className="text-sm text-gray-600">
                  Deseja salvar o rascunho (incluindo o status programado) antes de voltar à lista? Se sair sem salvar, o status
                  programado não será atualizado.
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row flex-wrap gap-3 justify-end">
              <button
                type="button"
                onClick={() => {
                  setRascunhoLeaveModalOpen(false);
                  void persistNewsDraftRef.current({ keepOnForm: false, includeStatusProgramado: true });
                }}
                className="px-4 py-2.5 rounded-xl font-medium text-white bg-orange-500 hover:bg-orange-600 transition-colors order-1"
              >
                Salvar e sair
              </button>
              <button
                type="button"
                onClick={() => {
                  setRascunhoLeaveModalOpen(false);
                  setDirty(false);
                  setViewMode('list');
                  setEditingId(null);
                }}
                className="px-4 py-2.5 rounded-xl font-medium text-gray-800 bg-white border border-gray-300 hover:bg-gray-50 transition-colors order-2"
              >
                Sair
              </button>
              <button
                type="button"
                onClick={() => setRascunhoLeaveModalOpen(false)}
                className="px-4 py-2.5 rounded-xl font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors order-3"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {categoryModalOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-[320] flex items-center justify-center p-4"
          onClick={() => setCategoryModalOpen(false)}
        >
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6" onClick={(event) => event.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              {modalCategory.id ? 'Editar categoria' : 'Nova categoria'}
            </h3>
            <p className="text-sm text-gray-600 mb-3">
              {modalCategory.id ? 'Altere o nome da categoria.' : 'Cadastre uma categoria padrão para matérias.'}
            </p>
            <input
              type="text"
              value={modalCategory.name}
              onChange={(event) => setModalCategory((prev) => ({ ...prev, name: event.target.value }))}
              className="w-full py-2 px-3 rounded-lg border border-gray-300"
              placeholder="Nome da categoria"
            />
            <div className="flex gap-3 justify-end mt-4">
              <button
                type="button"
                onClick={() => setCategoryModalOpen(false)}
                className="px-4 py-2.5 rounded-xl font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => {
                  void saveModalCategory();
                }}
                className="px-4 py-2.5 rounded-xl font-medium text-white bg-orange-500 hover:bg-orange-600 transition-colors"
              >
                Salvar
              </button>
            </div>
            {modalCategory.id && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    const cat = categories.find((item) => item.id === modalCategory.id);
                    if (cat) void handleDeleteCategory(cat);
                  }}
                  className="text-red-600 text-sm hover:underline"
                >
                  Excluir categoria
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {subcategoryModalOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-[320] flex items-center justify-center p-4"
          onClick={() => setSubcategoryModalOpen(false)}
        >
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6" onClick={(event) => event.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              {modalSubcategory.id ? 'Editar subcategoria' : 'Nova subcategoria'}
            </h3>
            <p className="text-sm text-gray-600 mb-3">Associe a subcategoria a uma categoria.</p>
            <div className="space-y-3">
              <select
                value={modalSubcategory.categoryId}
                onChange={(event) => setModalSubcategory((prev) => ({ ...prev, categoryId: event.target.value }))}
                className="w-full py-2 px-3 rounded-lg border border-gray-300"
              >
                <option value="">Selecione a categoria</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
              <input
                type="text"
                value={modalSubcategory.name}
                onChange={(event) => setModalSubcategory((prev) => ({ ...prev, name: event.target.value }))}
                className="w-full py-2 px-3 rounded-lg border border-gray-300"
                placeholder="Nome da subcategoria"
              />
            </div>
            <div className="flex gap-3 justify-end mt-4">
              <button
                type="button"
                onClick={() => setSubcategoryModalOpen(false)}
                className="px-4 py-2.5 rounded-xl font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => {
                  void saveModalSubcategory();
                }}
                className="px-4 py-2.5 rounded-xl font-medium text-white bg-orange-500 hover:bg-orange-600 transition-colors"
              >
                Salvar
              </button>
            </div>
            {modalSubcategory.id && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    const sub = subcategories.find((item) => item.id === modalSubcategory.id);
                    if (sub) void handleDeleteSubcategory(sub);
                  }}
                  className="text-red-600 text-sm hover:underline"
                >
                  Excluir subcategoria
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {categoriasManagerOpen && !reassignModal && (
        <div
          className="fixed inset-0 bg-black/50 z-[320] flex items-center justify-center p-4"
          onClick={() => setCategoriasManagerOpen(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[85vh] overflow-y-auto p-6"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex gap-4 mb-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Gerenciar categorias</h3>
                <p className="text-sm text-gray-600">Crie, edite ou exclua categorias e subcategorias de notícias.</p>
              </div>
            </div>
            <div className="flex gap-2 mb-4">
              <button
                type="button"
                onClick={() => {
                  setCategoriasManagerOpen(false);
                  openModalCategory();
                }}
                className="px-3 py-2 text-sm text-orange-600 border border-orange-300 rounded-lg hover:bg-orange-50"
              >
                Nova categoria
              </button>
              <button
                type="button"
                onClick={() => {
                  setCategoriasManagerOpen(false);
                  openModalSubcategory();
                }}
                className="px-3 py-2 text-sm text-orange-600 border border-orange-300 rounded-lg hover:bg-orange-50"
              >
                Nova subcategoria
              </button>
            </div>
            <div className="space-y-4">
              {categories.map((cat) => (
                <div key={cat.id} className="border border-gray-200 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2 gap-2">
                    <span className="font-medium text-gray-900">{cat.name}</span>
                    <div className="flex gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => {
                          setCategoriasManagerOpen(false);
                          openModalCategory(cat);
                        }}
                        className="px-2 py-1 text-xs text-gray-700 hover:bg-gray-100 rounded-lg"
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleDeleteCategory(cat)}
                        className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded-lg"
                      >
                        Excluir
                      </button>
                    </div>
                  </div>
                  <div className="space-y-1 pl-2 border-l-2 border-gray-200">
                    {getNewsSubcategoriesByCategory(cat.id).map((sub) => (
                      <div key={sub.id} className="flex items-center justify-between py-1 gap-2">
                        <span className="text-sm text-gray-600">{sub.name}</span>
                        <div className="flex gap-1 shrink-0">
                          <button
                            type="button"
                            onClick={() => {
                              setCategoriasManagerOpen(false);
                              openModalSubcategory(sub);
                            }}
                            className="px-2 py-0.5 text-xs text-gray-700 hover:bg-gray-100 rounded"
                          >
                            Editar
                          </button>
                          <button
                            type="button"
                            onClick={() => void handleDeleteSubcategory(sub)}
                            className="px-2 py-0.5 text-xs text-red-600 hover:bg-red-50 rounded"
                          >
                            Excluir
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-end mt-4">
              <button
                type="button"
                onClick={() => setCategoriasManagerOpen(false)}
                className="px-4 py-2.5 rounded-xl font-medium text-gray-700 bg-gray-100 hover:bg-gray-200"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {reassignModal && (
        <div
          className="fixed inset-0 bg-black/50 z-[350] flex items-center justify-center p-4"
          onClick={() => setReassignModal(null)}
        >
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6" onClick={(event) => event.stopPropagation()}>
            <div className="flex gap-4 mb-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Reatribuir antes de excluir</h3>
                <p className="text-sm text-gray-600 mt-1">
                  {`${reassignModal.articlesCount} matéria(s) `}
                  em &quot;{reassignModal.name}&quot;. Escolha uma nova categoria e subcategoria para elas:
                </p>
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Nova categoria</label>
                <select
                  value={reassignNewCategoryId}
                  onChange={(event) => {
                    setReassignNewCategoryId(event.target.value);
                    setReassignNewSubcategoryId('');
                  }}
                  className="w-full py-2 px-3 rounded-lg border border-gray-300"
                >
                  <option value="">Selecione</option>
                  {categories
                    .filter((c) => reassignModal.type !== 'category' || c.id !== reassignModal.id)
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                </select>
                {reassignModal.type === 'category' &&
                  categories.filter((c) => c.id !== reassignModal.id).length === 0 && (
                    <p className="text-xs text-amber-700 mt-1">Cadastre outra categoria antes de excluir esta.</p>
                  )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Nova subcategoria</label>
                <select
                  value={reassignNewSubcategoryId}
                  onChange={(event) => setReassignNewSubcategoryId(event.target.value)}
                  className="w-full py-2 px-3 rounded-lg border border-gray-300"
                >
                  <option value="">Selecione</option>
                  {reassignNewCategoryId &&
                    getNewsSubcategoriesByCategory(reassignNewCategoryId)
                      .filter((s) => reassignModal.type !== 'subcategory' || s.id !== reassignModal.id)
                      .map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                </select>
              </div>
            </div>
            <div className="flex gap-3 justify-end mt-4">
              <button
                type="button"
                onClick={() => setReassignModal(null)}
                className="px-4 py-2.5 rounded-xl font-medium text-gray-700 bg-gray-100 hover:bg-gray-200"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => void confirmReassignAndDelete()}
                disabled={!reassignNewCategoryId || !reassignNewSubcategoryId}
                className="px-4 py-2.5 rounded-xl font-medium text-white bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Reatribuir e excluir
              </button>
            </div>
          </div>
        </div>
      )}

      {listaStatusDropdown &&
        listaStatusDropdownArticle &&
        listaStatusDropdownModo &&
        typeof document !== 'undefined' &&
        document.body &&
        createPortal(
          <div
            id="noticias-lista-status-dropdown-portal"
            role="listbox"
            className={STATUS_MODO_LISTA_DROPDOWN_CLASS}
            style={{ top: listaStatusDropdown.top, left: listaStatusDropdown.left }}
          >
            {STATUS_MODO_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                role="option"
                aria-selected={opt.id === listaStatusDropdownModo}
                onMouseDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  applyModoStatusFromLista(listaStatusDropdown.articleId, opt.id);
                }}
                className={`flex w-full items-center gap-2 py-2 text-left text-xs font-medium whitespace-nowrap ${
                  opt.id === listaStatusDropdownModo
                    ? 'bg-orange-50 text-orange-900'
                    : 'bg-white text-gray-800 hover:bg-gray-50'
                }`}
              >
                <span className={`rounded-full h-2 w-2 shrink-0 ${statusModoDotClass(opt.id)}`} />
                <span>{opt.label}</span>
              </button>
            ))}
          </div>,
          document.body
        )}
    </AdminLayout>
  );
}
